import { ASSET_REF_FULL_RE, ASSET_REF_RE, ATTR_PAIR_RE, BLOCK_RENDER_RE, COMPLETION_DIRECTIVE_TRIGGER_RE, COMPLETION_PLUGIN_TRIGGER_RE, COMPLETION_REF_TRIGGER_RE, COMPLETION_RENDER_TRIGGER_RE, CONTENT_HEADER_RE, DEFINE_CLOSE_RE, DEFINE_HEADER_RE, DEFINE_ID_RE, FENCE_CLOSE_RE, FENCE_OPEN_RE, FOR_CLOSE_RE, FOR_HEADER_RE, IMPORT_RE, INLINE_RENDER_RE, MD_FENCE_OPEN_RE, META_HEADER_RE, REF_FULL_RE, REF_ID_RE, REF_RE, REF_STRUCTURED_RE, UNIT_IDENTITY_REF_RE, USE_HEADER_RE, computeFenceLength, getMarkdownCodeSpanRanges, getMarkdownFencedRanges, isClosingFence, isEscaped, isInsideCodeSpanInLine, isInsideInlineCode, isInsideMarkdownCodeSpan, isInsideMarkdownFencedBlock, isMarkdownFenceClose, makeFence, parseAttrs, parseDefineHeader, parseMarkdownFenceOpen } from "./patterns.js";
import { C as applyTemplateInputRefsToString, D as substituteTemplateInputRefs, E as hasTemplateInputRefs, S as INPUT_REF_RE, T as extractTemplateParams, _ as inferTemplateInputSchema, a as getDocumentAssetsDir, b as parseTemplateInputSchemaJson, c as isAssetRefPath, d as resolveDocumentAssetPath, f as TEMPLATE_INPUTS_FENCE_RE, g as getTemplateInputSchemaParams, h as getRequiredTemplateInputs, i as ASSETS_REF_PREFIX, l as normalizeAssetRefPath, m as getBlockingTemplateParams, n as scanDocument, o as getDocumentBundleDir, p as buildDefaultedTemplateInputs, r as scanDocumentText, s as getPathDirname, t as getDefinitionIdsFromText, u as parseAssetRefPath, v as mergeTemplateParams, w as applyTemplateInputRefsToValue, x as validateTemplateInputValues, y as normalizeTemplateInputSchema } from "./scanner-CYSPMwWj.js";
import { directiveFromMarkdown } from "mdast-util-directive";
import { fromMarkdown } from "mdast-util-from-markdown";
import { directive } from "micromark-extension-directive";
//#region src/parser/flatten.ts
/**
* 展平 DocumentNode 树为 DocumentBlock 数组。
* Container 和 Unknown 节点降级为 markdown。
*/
function flattenTree(nodes) {
	const blocks = [];
	for (const node of nodes) switch (node.type) {
		case "markdown":
			blocks.push({
				type: "markdown",
				content: node.content,
				source: node.source
			});
			break;
		case "definition":
			blocks.push({
				type: "definition",
				id: node.id,
				definition: node.definition,
				source: node.source
			});
			break;
		case "inline-render":
		case "block-render":
			blocks.push({
				type: node.type,
				definitionId: node.definitionId,
				attrs: node.attrs,
				source: node.source
			});
			break;
		case "inline-ref":
			blocks.push({
				type: "inline-ref",
				refPath: node.refPath,
				definitionId: node.definitionId,
				accessor: node.accessor,
				outputPath: node.outputPath,
				source: node.source
			});
			break;
		case "container":
			if (node.directive === "for" && node.attrs.id) {
				const forBlock = {
					type: "for-block",
					id: node.attrs.id,
					each: node.attrs.each ?? "",
					of: node.attrs.of ?? "",
					limit: node.attrs.limit ? parseInt(node.attrs.limit) : void 0,
					offset: node.attrs.offset ? parseInt(node.attrs.offset) : void 0,
					concurrency: node.attrs.concurrency ? parseInt(node.attrs.concurrency) : void 0,
					template: node.attrs.template === "true" ? true : void 0,
					children: flattenTree(node.children),
					source: node.source
				};
				blocks.push(forBlock);
			} else blocks.push(...flattenTree(node.children));
			break;
		case "unknown-directive": blocks.push({
			type: "markdown",
			content: node.rawContent,
			source: node.source
		});
	}
	return blocks;
}
//#endregion
//#region src/output/external-ref.ts
const EXTERNAL_OUTPUT_FILE_PATTERN = /^.+\.json(?:\.br)?$/;
const WINDOWS_DRIVE_PREFIX_PATTERN = /^[a-zA-Z]:/;
const PATH_SEPARATOR_PATTERN = /[\\/]/;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;
/**
* Check whether a file-protocol locator is a supported output filename.
*
* External outputs intentionally live directly inside the bundle's
* `outputs/` directory. Locators are filenames rather than general paths,
* so accepting either POSIX or Windows separators would make their meaning
* platform-dependent and could allow directory traversal.
*/
function isValidExternalOutputLocator(value) {
	return typeof value === "string" && EXTERNAL_OUTPUT_FILE_PATTERN.test(value) && !PATH_SEPARATOR_PATTERN.test(value) && !WINDOWS_DRIVE_PREFIX_PATTERN.test(value) && !CONTROL_CHARACTER_PATTERN.test(value);
}
/**
* Validate a descriptor and every descriptor in its `related` graph.
*
* The iterative traversal avoids overflowing the call stack on malformed,
* deeply nested input. Cycles are rejected because descriptors must remain
* JSON-serializable.
*/
function isValidExternalOutputDescriptor(value) {
	const active = /* @__PURE__ */ new Set();
	const validated = /* @__PURE__ */ new Set();
	const stack = [{
		value,
		exiting: false
	}];
	while (stack.length > 0) {
		const frame = stack.pop();
		if (typeof frame.value !== "object" || frame.value === null) return false;
		const descriptor = frame.value;
		if (frame.exiting) {
			active.delete(descriptor);
			validated.add(descriptor);
			continue;
		}
		if (validated.has(descriptor)) continue;
		if (active.has(descriptor)) return false;
		if (descriptor.protocol !== "file" || !isValidExternalOutputLocator(descriptor.locator)) return false;
		const related = descriptor.related;
		if (related !== void 0 && !Array.isArray(related)) return false;
		active.add(descriptor);
		stack.push({
			value: descriptor,
			exiting: true
		});
		if (related) for (let index = related.length - 1; index >= 0; index--) stack.push({
			value: related[index],
			exiting: false
		});
	}
	return true;
}
/**
* Strict type guard: is `value` a pure external-output pointer?
*
* A "pure stub" is an object whose only keys are `__`-prefixed and where
* `__external` is a well-formed `ExternalOutputDescriptor`.  We require
* the pure-stub shape so that real outputs which happen to carry an
* `__external` metadata field (legacy contamination) are not misclassified.
*/
function isExternalStub(value) {
	if (typeof value !== "object" || value === null) return false;
	if (!("__external" in value)) return false;
	const ext = value.__external;
	if (!isValidExternalOutputDescriptor(ext)) return false;
	for (const k of Object.keys(value)) if (!k.startsWith("__")) return false;
	return true;
}
/**
* Split a parsed output value into `{ externalRef, output }`.
*
* If `value` is a pure external stub, the descriptor is lifted into
* `externalRef` and `output` becomes `undefined` — the engine hydrator
* will read the bundle file and populate `output` later.
*
* Otherwise `output` passes through unchanged (nested stubs inside a
* non-pure object are still handled by the engine's `deepHydrate`).
*/
function extractExternalRef(value) {
	if (value === void 0) return { output: void 0 };
	if (isExternalStub(value)) return {
		externalRef: value.__external,
		output: void 0
	};
	return { output: value };
}
const LARGE_INLINE_OUTPUT_DIAGNOSTIC_CODE = "output-large-inline";
const BYTE_FORMATTER = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
function createLargeInlineOutputDiagnostic(params) {
	if (params.externalRef) return void 0;
	if (params.output === void 0) return void 0;
	const sizeBytes = getJsonByteLength(params.output);
	if (sizeBytes < (params.thresholdBytes ?? 262144)) return void 0;
	return {
		line: params.line,
		severity: "warning",
		message: `${params.kind} "${params.id}" inline output is ${formatBytes(sizeBytes)}. ` + buildSuggestion(params.outputMode),
		code: LARGE_INLINE_OUTPUT_DIAGNOSTIC_CODE
	};
}
function getJsonByteLength(value) {
	const json = JSON.stringify(value);
	if (json === void 0) return 0;
	return new TextEncoder().encode(json).byteLength;
}
function formatBytes(bytes) {
	if (bytes >= 1048576) return `${BYTE_FORMATTER.format(bytes / 1048576)} MB`;
	return `${BYTE_FORMATTER.format(bytes / 1024)} KB`;
}
function buildSuggestion(outputMode) {
	if (outputMode === "external") return "Large inline outputs slow open/save/completion; rerun the unit to persist this output outside Markdown.";
	return "Large inline outputs slow open/save/completion; add output=\"external\" and rerun the unit to persist the output outside Markdown.";
}
//#endregion
//#region src/parser/handlers/content-handler.ts
/**
* ::content[id] 块处理器
*
* 解析内容定义块：
*   content-header → body lines → (optional output fence) → close
*
* 与 plugin define 不同：
* - 无 config 围栏（body 直接是 Markdown）
* - 可选 output 围栏（scope 聚合输出，在 :: 闭合之前）
* - body 可包含嵌套 ::define / ::content / ::use / ::meta 块
*/
/** 匹配一定需要用 :: 闭合的块开头，用于嵌套深度追踪。 */
const BLOCK_OPENER_RE$1 = /^::(define\[|content\[|meta\[|use\[)/;
const IMPORT_OPENER_RE = /^::import\{/;
function isBlockFormImport$1(lines, headerIndex) {
	let cursor = headerIndex + 1;
	while (cursor < lines.length && lines[cursor].trim() === "") cursor++;
	if (cursor >= lines.length) return false;
	const next = lines[cursor].trim();
	if (/^::\s*$/.test(next)) return true;
	return FENCE_OPEN_RE.exec(next)?.[2] === "output";
}
/**
* 判断是否是 ::content[id] 头部
*/
function isContentHeader(trimmed) {
	return CONTENT_HEADER_RE.test(trimmed);
}
/**
* 从行数组的 startIndex 位置开始，解析一个 ::content[id] 块。
*/
function parseContentBlock(lines, startIndex) {
	const headerLine = lines[startIndex];
	const trimmedHeader = headerLine.trim();
	const headerMatch = CONTENT_HEADER_RE.exec(trimmedHeader);
	if (!headerMatch) return {
		consumed: 1,
		fallbackLines: [headerLine]
	};
	const id = headerMatch[1];
	const attrsStr = headerMatch[2] ?? "";
	const attrs = attrsStr ? parseAttrs(attrsStr) : {};
	const diagnostics = [];
	const isTemplate = attrs.template === "true";
	const outputMode = attrs.output === "external" || attrs.output === "inline" ? attrs.output : void 0;
	const bodyLines = [];
	let inputsJson;
	let outputJson;
	let nestingDepth = 0;
	let forNestingDepth = 0;
	let i = startIndex + 1;
	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();
		if (nestingDepth === 0 && forNestingDepth === 0) {
			const inputsFence = TEMPLATE_INPUTS_FENCE_RE.exec(trimmed);
			if (inputsFence) {
				const fenceLen = inputsFence[1].length;
				const inputLines = [];
				i++;
				while (i < lines.length) {
					const fLine = lines[i];
					const fTrimmed = fLine.trim();
					if (isClosingFence(fTrimmed, fenceLen)) break;
					inputLines.push(fLine);
					i++;
				}
				inputsJson = inputLines.join("\n");
				i++;
				continue;
			}
		}
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		const isContentOutputFence = fenceOpen?.info === "output" && nestingDepth === 0 && forNestingDepth === 0;
		if (fenceOpen && !isContentOutputFence) {
			bodyLines.push(line);
			i++;
			while (i < lines.length) {
				bodyLines.push(lines[i]);
				if (isMarkdownFenceClose(lines[i].trim(), fenceOpen)) {
					i++;
					break;
				}
				i++;
			}
			continue;
		}
		if (BLOCK_OPENER_RE$1.test(trimmed)) {
			nestingDepth++;
			bodyLines.push(line);
			i++;
			continue;
		}
		if (IMPORT_OPENER_RE.test(trimmed)) {
			if (isBlockFormImport$1(lines, i)) nestingDepth++;
			bodyLines.push(line);
			i++;
			continue;
		}
		if (FOR_HEADER_RE.test(trimmed)) {
			forNestingDepth++;
			bodyLines.push(line);
			i++;
			continue;
		}
		if (FOR_CLOSE_RE.test(trimmed)) {
			if (forNestingDepth > 0) forNestingDepth--;
			bodyLines.push(line);
			i++;
			continue;
		}
		if (/^::\s*$/.test(trimmed)) {
			if (nestingDepth > 0) {
				nestingDepth--;
				bodyLines.push(line);
				i++;
				continue;
			}
			if (forNestingDepth > 0) {
				bodyLines.push(line);
				i++;
				continue;
			}
			const body = bodyLines.join("\n");
			const parsedInputs = parseTemplateInputSchemaJson(inputsJson, startIndex + 1, `::content[${id}]`);
			const inputSchema = parsedInputs.schema;
			diagnostics.push(...parsedInputs.diagnostics);
			const params = mergeTemplateParams(extractTemplateParams(body), inputSchema);
			if (params.length > 0 && !isTemplate) diagnostics.push({
				line: startIndex + 1,
				severity: "warning",
				message: `::content[${id}] body uses $ref{input/...} but is not marked template="true"`,
				code: "content-missing-template"
			});
			let output;
			let externalRef;
			if (outputJson !== void 0) {
				const trimmedOutput = outputJson.trim();
				if (trimmedOutput) try {
					const parsed = JSON.parse(trimmedOutput);
					const split = extractExternalRef(parsed);
					output = split.output;
					externalRef = split.externalRef;
					const largeInlineOutputDiagnostic = createLargeInlineOutputDiagnostic({
						line: startIndex + 1,
						kind: "content",
						id,
						output: parsed,
						outputMode,
						externalRef
					});
					if (largeInlineOutputDiagnostic) diagnostics.push(largeInlineOutputDiagnostic);
				} catch (e) {
					diagnostics.push({
						line: startIndex + 1,
						severity: "warning",
						message: `::content[${id}] output JSON parse failed: ${e.message}`,
						code: "content-invalid-output"
					});
				}
			}
			const definition = {
				id,
				kind: "content",
				template: isTemplate,
				body,
				params: params.length > 0 ? params : void 0,
				...inputSchema ? { inputSchema } : {},
				range: {
					start: startIndex + 1,
					end: i + 1
				},
				output,
				outputMode,
				...externalRef ? { externalRef } : {}
			};
			return {
				consumed: i - startIndex + 1,
				node: {
					type: "content-definition",
					definition
				},
				diagnostics: diagnostics.length > 0 ? diagnostics : void 0
			};
		}
		if (nestingDepth === 0 && forNestingDepth === 0) {
			const fenceMatch = FENCE_OPEN_RE.exec(trimmed);
			if (fenceMatch && fenceMatch[2] === "output") {
				const fenceLen = fenceMatch[1].length;
				const outputLines = [];
				i++;
				while (i < lines.length) {
					const fLine = lines[i];
					const fTrimmed = fLine.trim();
					if (isClosingFence(fTrimmed, fenceLen)) break;
					outputLines.push(fLine);
					i++;
				}
				outputJson = outputLines.join("\n");
				i++;
				continue;
			}
		}
		bodyLines.push(line);
		i++;
	}
	diagnostics.push({
		line: startIndex + 1,
		severity: "error",
		message: `::content[${id}] block is not closed; missing ::`,
		code: "content-unclosed"
	});
	return {
		consumed: lines.length - startIndex,
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0,
		fallbackLines: lines.slice(startIndex, lines.length)
	};
}
//#endregion
//#region src/parser/handlers/define-handler.ts
/**
* ::define 块处理器
*
* 从行流中解析 ::define 块，使用状态机处理：
*   define-header → optional config → optional output → done
*/
/**
* 判断一行（trimmed）是否是 define header
*/
function isDefineHeader(trimmed) {
	return DEFINE_HEADER_RE.test(trimmed);
}
/**
* 从行数组的 startIndex 位置开始，解析一个 ::define 块。
* startIndex 指向 header 行。
*/
function parseDefineBlock(lines, startIndex) {
	const headerLine = lines[startIndex];
	const trimmedHeader = headerLine.trim();
	const parsed = parseDefineHeader(trimmedHeader);
	if (!parsed) return {
		consumed: 1,
		fallbackLines: [headerLine]
	};
	const { id, pluginName, outputMode, configMode, template } = parsed;
	let state = "expect-fence-or-close";
	const configLines = [];
	const inputsLines = [];
	const outputLines = [];
	let configFenceLen = 3;
	let inputsFenceLen = 3;
	let outputFenceLen = 3;
	let outputFenceLine;
	let i = startIndex + 1;
	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();
		switch (state) {
			case "expect-fence-or-close":
			case "after-fence": {
				const fmH = FENCE_OPEN_RE.exec(trimmed);
				if (fmH && fmH[2] === "config") {
					configFenceLen = fmH[1].length;
					state = "in-config";
				} else if (TEMPLATE_INPUTS_FENCE_RE.test(trimmed)) {
					inputsFenceLen = TEMPLATE_INPUTS_FENCE_RE.exec(trimmed)?.[1].length ?? 3;
					state = "in-inputs";
				} else if (fmH && fmH[2] === "output") {
					outputFenceLen = fmH[1].length;
					outputFenceLine = i + 1;
					state = "in-output";
				} else if (trimmed === "::") return finishDefine(id, pluginName, configLines, inputsLines, outputLines, i - startIndex + 1, lines.slice(startIndex, i + 1), startIndex + 1, outputMode, configMode, template === true, outputFenceLine);
				else if (trimmed === "") {} else return {
					consumed: i - startIndex + 1,
					fallbackLines: lines.slice(startIndex, i + 1)
				};
				break;
			}
			case "in-config":
				if (isClosingFence(trimmed, configFenceLen)) state = "after-fence";
				else configLines.push(line);
				break;
			case "in-inputs":
				if (isClosingFence(trimmed, inputsFenceLen)) state = "after-fence";
				else inputsLines.push(line);
				break;
			case "in-output": if (isClosingFence(trimmed, outputFenceLen)) state = "after-fence";
			else outputLines.push(line);
		}
		i++;
	}
	return {
		consumed: lines.length - startIndex,
		fallbackLines: lines.slice(startIndex)
	};
}
function finishDefine(id, pluginName, configLines, inputsLines, outputLines, consumed, rawLines, line, outputMode, configMode, isTemplate = false, outputFenceLine) {
	while (configLines.length && !configLines[0].trim()) configLines.shift();
	while (configLines.length && !configLines[configLines.length - 1].trim()) configLines.pop();
	while (outputLines.length && !outputLines[0].trim()) outputLines.shift();
	while (outputLines.length && !outputLines[outputLines.length - 1].trim()) outputLines.pop();
	const configJson = configLines.join("\n");
	const inputsJson = inputsLines.length > 0 ? inputsLines.join("\n") : void 0;
	const outputJson = outputFenceLine !== void 0 ? outputLines.join("\n") : void 0;
	try {
		const parsedConfig = configJson.trim() ? JSON.parse(configJson) : {};
		let config = parsedConfig;
		let externalConfigRef;
		let configExternalError;
		if (isExternalStub(parsedConfig)) {
			externalConfigRef = parsedConfig.__external;
			config = {};
		} else if (typeof parsedConfig === "object" && parsedConfig !== null && "__external" in parsedConfig) {
			configExternalError = `::define[${id}] config __external must be a pure root stub`;
			config = {};
		}
		const parsedOutputResult = outputFenceLine !== void 0 ? parseOutput(outputJson ?? "") : {};
		const parsedOutput = parsedOutputResult.output;
		const { externalRef, output } = extractExternalRef(parsedOutput);
		const parsedInputs = parseTemplateInputSchemaJson(inputsJson, line, `::define[${id}]`);
		const inputSchema = parsedInputs.schema;
		const params = mergeTemplateParams(extractTemplateParams(config), inputSchema);
		const diagnostics = [];
		if (configExternalError) diagnostics.push({
			line,
			severity: "error",
			message: configExternalError,
			code: "config-external-invalid-stub"
		});
		diagnostics.push(...parsedInputs.diagnostics);
		if (parsedOutputResult.error) diagnostics.push({
			line: outputFenceLine ?? line,
			severity: "error",
			message: `::define[${id}] output JSON parse failed: ${parsedOutputResult.error}`,
			code: "define-invalid-output-json"
		});
		const largeInlineOutputDiagnostic = createLargeInlineOutputDiagnostic({
			line,
			kind: "definition",
			id,
			output: parsedOutput,
			outputMode,
			externalRef
		});
		if (largeInlineOutputDiagnostic) diagnostics.push(largeInlineOutputDiagnostic);
		if (params.length > 0 && !isTemplate) diagnostics.push({
			line,
			severity: "warning",
			message: `::define[${id}] config uses $ref{input/...} but is not marked template="true"`,
			code: "define-missing-template"
		});
		const definition = {
			id,
			pluginName,
			config,
			...isTemplate ? { template: true } : {},
			...params.length > 0 ? { params } : {},
			...inputSchema ? { inputSchema } : {},
			output,
			outputMode,
			configMode,
			...externalConfigRef ? { externalConfigRef } : {},
			...configExternalError ? { configExternalError } : {},
			...externalRef ? { externalRef } : {}
		};
		return {
			consumed,
			node: {
				type: "definition",
				id,
				definition
			},
			definition,
			diagnostics: diagnostics.length > 0 ? diagnostics : void 0
		};
	} catch (e) {
		return {
			consumed,
			diagnostics: [{
				line,
				severity: "error",
				message: `::define[${id}] config JSON parse failed: ${e instanceof Error ? e.message : String(e)}`,
				code: "define-invalid-json"
			}],
			fallbackLines: rawLines
		};
	}
}
function parseOutput(json) {
	try {
		return { output: JSON.parse(json) };
	} catch (error) {
		return { error: error instanceof Error ? error.message : String(error) };
	}
}
//#endregion
//#region src/parser/handlers/render-handler.ts
/**
* ::render 指令处理器
*
* 处理 block-render（独占一行）和 inline-render（行内）两种形态。
*/
/**
* 解析 block render 行
*/
function parseBlockRender(trimmed) {
	const m = BLOCK_RENDER_RE.exec(trimmed);
	if (!m) return null;
	return {
		type: "block-render",
		definitionId: m[1],
		attrs: parseAttrs(m[2])
	};
}
/**
* 提取内容中的 inline render 匹配项
* 返回 { index, length, node } 数组
*/
function findInlineRenders(content, isInsideInlineCode, isEscaped) {
	const results = [];
	const pattern = new RegExp(INLINE_RENDER_RE.source, "g");
	let match;
	while ((match = pattern.exec(content)) !== null) {
		const [fullMatch, definitionId, attrsStr] = match;
		if (isEscaped(content, match.index) || isInsideInlineCode(content, match.index)) continue;
		const lineStart = content.lastIndexOf("\n", match.index) + 1;
		const lineEnd = content.indexOf("\n", match.index + fullMatch.length);
		if (content.slice(lineStart, lineEnd === -1 ? content.length : lineEnd).trim() === fullMatch) continue;
		results.push({
			index: match.index,
			length: fullMatch.length,
			node: {
				type: "inline-render",
				definitionId,
				attrs: parseAttrs(attrsStr)
			}
		});
	}
	return results;
}
//#endregion
//#region src/parser/handlers/paragraph-handler.ts
/**
* paragraph / content 处理器
*
* 负责在自由文本中找出：
* 1. block-render（独占一行的 ::render[id]::）
* 2. inline-render（行内 ::render[id]::）
* 3. inline-ref（$ref{id.output.path} / $ref{unit/id}）
*
* 将纯文本内容拆分为 markdown / inline-render / inline-ref 节点的混合序列。
*/
/** Module-level cached regex: BLOCK_RENDER_RE with gm flags for multi-line scanning.
*  Allows leading/trailing whitespace so indented content (e.g. inside ::for bodies)
*  is still recognized as block-level render markers. */
const BLOCK_RENDER_GM = /^\s*::render\[(\w[\w-]*(?:\/\w[\w-]*)*)\](\{[^}]*\})?::\s*$/gm;
function createInlineCodeLookup(content, buildRanges = getMarkdownCodeSpanRanges) {
	const ranges = buildRanges(content);
	return (pos) => isInsideMarkdownCodeSpan(ranges, pos);
}
function createSourceSpan(context, localStart, localEnd) {
	if (!context) return void 0;
	const startOffset = context.toSourceOffset?.(localStart) ?? context.startOffset + localStart;
	const endOffset = context.toSourceOffset?.(localEnd) ?? context.startOffset + localEnd;
	return {
		startOffset,
		endOffset,
		range: context.toRange(startOffset, endOffset)
	};
}
/**
* 解析一段内容文本，拆分为 DocumentNode 数组。
* 处理 block render、inline render、$ref{} 引用。
*/
function parseContent(content, _legacyIsInsideInlineCode, isEscaped, sourceContext) {
	const nodes = [];
	let lastIndex = 0;
	const fencedRanges = getMarkdownFencedRanges(content);
	const insideFence = (pos) => isInsideMarkdownFencedBlock(fencedRanges, pos);
	const insideCode = createInlineCodeLookup(content);
	BLOCK_RENDER_GM.lastIndex = 0;
	const blockPattern = BLOCK_RENDER_GM;
	let match;
	while ((match = blockPattern.exec(content)) !== null) {
		const [fullMatch] = match;
		if (isEscaped(content, match.index) || insideCode(match.index) || insideFence(match.index)) continue;
		if (match.index > lastIndex) {
			const before = content.slice(lastIndex, match.index);
			if (before.trim()) {
				const beforeOffset = lastIndex;
				const beforeInsideFence = (pos) => insideFence(pos + beforeOffset);
				const beforeInsideCode = (pos) => insideCode(pos + beforeOffset);
				parseInlineContent(before, nodes, beforeInsideCode, isEscaped, beforeInsideFence, sourceContext ? {
					startOffset: sourceContext.toSourceOffset?.(beforeOffset) ?? sourceContext.startOffset + beforeOffset,
					toRange: sourceContext.toRange,
					...sourceContext.toSourceOffset ? { toSourceOffset: (offset) => sourceContext.toSourceOffset(beforeOffset + offset) } : {}
				} : void 0);
			}
		}
		const blockNode = parseBlockRender(fullMatch.trim());
		if (blockNode) nodes.push({
			...blockNode,
			source: createSourceSpan(sourceContext, match.index, match.index + fullMatch.length)
		});
		lastIndex = match.index + fullMatch.length;
	}
	if (lastIndex < content.length) {
		const remaining = content.slice(lastIndex);
		if (remaining.trim()) {
			const remainingInsideFence = (pos) => insideFence(pos + lastIndex);
			const remainingInsideCode = (pos) => insideCode(pos + lastIndex);
			parseInlineContent(remaining, nodes, remainingInsideCode, isEscaped, remainingInsideFence, sourceContext ? {
				startOffset: sourceContext.toSourceOffset?.(lastIndex) ?? sourceContext.startOffset + lastIndex,
				toRange: sourceContext.toRange,
				...sourceContext.toSourceOffset ? { toSourceOffset: (offset) => sourceContext.toSourceOffset(lastIndex + offset) } : {}
			} : void 0);
		}
	}
	return nodes;
}
/**
* 解析 inline 内容：inline-render + $ref{} + markdown 文本
*/
function parseInlineContent(content, nodes, isInsideCode, isEscaped, insideFence, sourceContext) {
	const matches = [];
	const inlineRenders = findInlineRenders(content, (_content, pos) => isInsideCode(pos), isEscaped);
	for (const ir of inlineRenders) {
		if (insideFence?.(ir.index)) continue;
		matches.push({
			index: ir.index,
			length: ir.length,
			node: {
				...ir.node,
				source: createSourceSpan(sourceContext, ir.index, ir.index + ir.length)
			}
		});
	}
	REF_STRUCTURED_RE.lastIndex = 0;
	const refPattern = REF_STRUCTURED_RE;
	let match;
	while ((match = refPattern.exec(content)) !== null) {
		const [fullMatch, definitionId, accessor, subPath] = match;
		const normalizedAccessor = accessor === "content" ? "body" : accessor;
		if (isEscaped(content, match.index) || isInsideCode(match.index) || insideFence?.(match.index)) continue;
		matches.push({
			index: match.index,
			length: fullMatch.length,
			node: {
				type: "inline-ref",
				refPath: fullMatch.slice(5, -1),
				definitionId,
				accessor: normalizedAccessor,
				outputPath: subPath ?? "",
				source: createSourceSpan(sourceContext, match.index, match.index + fullMatch.length)
			}
		});
	}
	UNIT_IDENTITY_REF_RE.lastIndex = 0;
	const unitRefPattern = UNIT_IDENTITY_REF_RE;
	while ((match = unitRefPattern.exec(content)) !== null) {
		const [fullMatch, unitId] = match;
		if (isEscaped(content, match.index) || isInsideCode(match.index) || insideFence?.(match.index)) continue;
		matches.push({
			index: match.index,
			length: fullMatch.length,
			node: {
				type: "inline-ref",
				refPath: fullMatch.slice(5, -1),
				definitionId: unitId,
				accessor: "unit",
				outputPath: "",
				source: createSourceSpan(sourceContext, match.index, match.index + fullMatch.length)
			}
		});
	}
	matches.sort((a, b) => a.index - b.index);
	let lastIndex = 0;
	for (const m of matches) {
		if (m.index < lastIndex) continue;
		if (m.index > lastIndex) {
			const before = content.slice(lastIndex, m.index);
			if (before.length > 0) nodes.push({
				type: "markdown",
				content: before,
				source: createSourceSpan(sourceContext, lastIndex, m.index)
			});
		}
		nodes.push(m.node);
		lastIndex = m.index + m.length;
	}
	if (lastIndex < content.length) {
		const remaining = content.slice(lastIndex);
		if (remaining.length > 0) nodes.push({
			type: "markdown",
			content: remaining,
			source: createSourceSpan(sourceContext, lastIndex, content.length)
		});
	}
}
//#endregion
//#region src/parser/handlers/use-handler.ts
/**
* ::use 块处理器
*
* 解析 ::use[id]{from="..."} 块：
*   use-header → (optional config-fence) → (optional output-fence) → close
*
* config 围栏可选，包含 with/patch/bind JSON。
* output 围栏可选，包含物化后的执行结果。
*/
/**
* 判断是否是 ::use[id]{...} 头部
*/
function isUseHeader(trimmed) {
	return USE_HEADER_RE.test(trimmed);
}
/**
* 从行数组的 startIndex 位置开始，解析一个 ::use 块。
*/
function parseUseBlock(lines, startIndex) {
	const headerLine = lines[startIndex];
	const trimmedHeader = headerLine.trim();
	const headerMatch = USE_HEADER_RE.exec(trimmedHeader);
	if (!headerMatch) return {
		consumed: 1,
		fallbackLines: [headerLine]
	};
	const id = headerMatch[1];
	const attrsStr = headerMatch[2];
	const attrs = parseAttrs(attrsStr);
	const diagnostics = [];
	const isTemplate = attrs.template === "true";
	const outputMode = attrs.output === "external" || attrs.output === "inline" ? attrs.output : void 0;
	const configMode = attrs.config === "external" || attrs.config === "inline" ? attrs.config : void 0;
	if (!attrs.from) {
		diagnostics.push({
			line: startIndex + 1,
			severity: "error",
			message: "::use is missing required attribute from",
			code: "use-missing-from"
		});
		return {
			consumed: 1,
			diagnostics,
			fallbackLines: [headerLine]
		};
	}
	let configLines = [];
	let inputsLines = [];
	let outputLines = [];
	let inFence = false;
	let fenceLength = 0;
	let fenceKind = "";
	let hasConfig = false;
	let hasInputs = false;
	let hasOutput = false;
	let i = startIndex + 1;
	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();
		if (inFence) {
			if (isClosingFence(trimmed, fenceLength)) {
				inFence = false;
				fenceKind = "";
				i++;
				continue;
			}
			if (fenceKind === "config") configLines.push(line);
			else if (fenceKind === "inputs") inputsLines.push(line);
			else if (fenceKind === "output") outputLines.push(line);
			i++;
			continue;
		}
		if (/^::\s*$/.test(trimmed)) {
			let withParams;
			let patch;
			let bind;
			let externalConfigRef;
			let configExternalError;
			if (hasConfig) {
				const configText = configLines.join("\n").trim();
				if (configText) try {
					const parsed = JSON.parse(configText);
					if (isExternalStub(parsed)) externalConfigRef = parsed.__external;
					else if (typeof parsed === "object" && parsed !== null && "__external" in parsed) {
						configExternalError = `::use[${id}] config __external must be a pure root stub`;
						diagnostics.push({
							line: startIndex + 1,
							severity: "error",
							message: configExternalError,
							code: "config-external-invalid-stub"
						});
					} else {
						if (parsed.with) withParams = parsed.with;
						if (parsed.patch) patch = parsed.patch;
						if (parsed.bind) bind = parsed.bind;
					}
				} catch (e) {
					diagnostics.push({
						line: startIndex + 1,
						severity: "error",
						message: `::use[${id}] config JSON parse failed: ${e.message}`,
						code: "use-invalid-json"
					});
				}
			}
			const parsedInputs = parseTemplateInputSchemaJson(hasInputs ? inputsLines.join("\n") : void 0, startIndex + 1, `::use[${id}]`);
			const inputSchema = parsedInputs.schema;
			diagnostics.push(...parsedInputs.diagnostics);
			const params = mergeTemplateParams(extractTemplateParams({
				with: withParams,
				patch,
				bind
			}), inputSchema);
			if (params.length > 0 && !isTemplate) diagnostics.push({
				line: startIndex + 1,
				severity: "warning",
				message: `::use[${id}] config uses $ref{input/...} but is not marked template="true"`,
				code: "use-missing-template"
			});
			let output;
			let externalRef;
			if (hasOutput) {
				const outputText = outputLines.join("\n").trim();
				if (outputText) try {
					const parsed = JSON.parse(outputText);
					if (isExternalStub(parsed)) externalRef = parsed.__external;
					else if (parsed && typeof parsed === "object" && "kind" in parsed && "materializedId" in parsed && "sourceName" in parsed) {
						const split = extractExternalRef(parsed);
						output = split.output;
						if (split.externalRef) externalRef = split.externalRef;
						const largeInlineOutputDiagnostic = createLargeInlineOutputDiagnostic({
							line: startIndex + 1,
							kind: "use",
							id,
							output: parsed,
							outputMode,
							externalRef
						});
						if (largeInlineOutputDiagnostic) diagnostics.push(largeInlineOutputDiagnostic);
					} else {
						const legacy = {
							kind: "node",
							materializedId: id,
							sourceName: attrs.from,
							nodeOutput: parsed
						};
						output = legacy;
						const largeInlineOutputDiagnostic = createLargeInlineOutputDiagnostic({
							line: startIndex + 1,
							kind: "use",
							id,
							output: legacy,
							outputMode
						});
						if (largeInlineOutputDiagnostic) diagnostics.push(largeInlineOutputDiagnostic);
					}
				} catch (e) {
					diagnostics.push({
						line: startIndex + 1,
						severity: "warning",
						message: `::use[${id}] output JSON parse failed: ${e.message}`,
						code: "use-invalid-output"
					});
				}
			}
			const binding = {
				id,
				source: attrs.from,
				with: withParams,
				patch,
				bind,
				...isTemplate ? { template: true } : {},
				...params.length > 0 ? { params } : {},
				...inputSchema ? { inputSchema } : {},
				range: {
					start: startIndex + 1,
					end: i + 1
				},
				output,
				...outputMode ? { outputMode } : {},
				...configMode ? { configMode } : {},
				...externalConfigRef ? { externalConfigRef } : {},
				...configExternalError ? { configExternalError } : {},
				...externalRef ? { externalRef } : {}
			};
			return {
				consumed: i - startIndex + 1,
				node: {
					type: "use",
					binding
				},
				diagnostics: diagnostics.length > 0 ? diagnostics : void 0
			};
		}
		const inputsFenceMatch = TEMPLATE_INPUTS_FENCE_RE.exec(trimmed);
		const fenceMatch = FENCE_OPEN_RE.exec(trimmed);
		if (inputsFenceMatch || fenceMatch) {
			const kind = inputsFenceMatch ? "inputs" : fenceMatch[2];
			inFence = true;
			fenceLength = inputsFenceMatch?.[1].length ?? fenceMatch[1].length;
			fenceKind = kind;
			if (kind === "config") {
				configLines = [];
				hasConfig = true;
			} else if (kind === "inputs") {
				inputsLines = [];
				hasInputs = true;
			} else {
				outputLines = [];
				hasOutput = true;
			}
			i++;
			continue;
		}
		if (trimmed) diagnostics.push({
			line: i + 1,
			severity: "warning",
			message: `::use block should not contain non-config/output content: "${trimmed}"`,
			code: "use-unexpected-content"
		});
		i++;
	}
	diagnostics.push({
		line: startIndex + 1,
		severity: "error",
		message: `::use[${id}] block is not closed; missing ::`,
		code: "use-unclosed"
	});
	return {
		consumed: lines.length - startIndex,
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0,
		fallbackLines: lines.slice(startIndex, lines.length)
	};
}
//#endregion
//#region src/parser/handlers/for-handler.ts
/**
* ::for 块处理器
*
* 从行流中解析 ::for[id]{...} ... ::endfor 块。
* 内部的 ::define 等收纳到 ForDefinition.innerScope（不注册到顶层 definitions）。
* 内部的其他内容（markdown、::render 等）作为子节点保留在 Container.children 中。
*/
/**
* 判断一行（trimmed）是否是 ::for 头部
*/
function isForHeader(trimmed) {
	return FOR_HEADER_RE.test(trimmed);
}
/**
* 判断一行（trimmed）是否是 ::endfor
*/
function isForClose(trimmed) {
	return FOR_CLOSE_RE.test(trimmed);
}
/**
* 从行数组的 startIndex 位置开始，解析一个 ::for 块。
* startIndex 指向 ::for header 行。
*
* 支持嵌套 ::for，内层循环收纳到 outer for 的 innerScope。
*
* @param isInsideInlineCode  用于 parseContent 的回调
* @param isEscaped           用于 parseContent 的回调
*/
function parseForBlock(lines, startIndex, isInsideInlineCode, isEscaped) {
	const headerLine = lines[startIndex];
	const trimmedHeader = headerLine.trim();
	const headerMatch = FOR_HEADER_RE.exec(trimmedHeader);
	if (!headerMatch) return {
		consumed: 1,
		fallbackLines: [headerLine]
	};
	const forId = headerMatch[1];
	const attrsStr = headerMatch[2];
	const attrs = parseAttrs(attrsStr);
	const isTemplate = attrs.template === "true";
	if (!attrs.each || !attrs.of) return {
		consumed: 1,
		fallbackLines: [headerLine]
	};
	const children = [];
	const innerDefinitions = /* @__PURE__ */ new Map();
	const innerContentDefinitions = /* @__PURE__ */ new Map();
	const innerForDefinitions = /* @__PURE__ */ new Map();
	const innerUses = [];
	const diagnostics = [];
	const seenInnerExecutableIds = /* @__PURE__ */ new Map();
	const registerInnerExecutableId = (id, kind, line) => {
		const first = seenInnerExecutableIds.get(id);
		if (!first) {
			seenInnerExecutableIds.set(id, {
				kind,
				line
			});
			return;
		}
		diagnostics.push({
			line,
			severity: "error",
			message: `Duplicate executable ID "${id}"; first declared as ${first.kind} on line ${first.line}`,
			code: "duplicate-definition"
		});
	};
	let contentLines = [];
	let forOutputJson;
	let inputsJson;
	const flushContent = () => {
		const text = contentLines.join("\n");
		if (text.trim()) {
			const contentNodes = parseContent(text, isInsideInlineCode, isEscaped);
			children.push(...contentNodes);
		}
		contentLines = [];
	};
	let i = startIndex + 1;
	while (i < lines.length) {
		const trimmed = lines[i].trim();
		const inputsFence = TEMPLATE_INPUTS_FENCE_RE.exec(trimmed);
		if (inputsFence) {
			flushContent();
			const fenceLen = inputsFence[1].length;
			const inputLines = [];
			i++;
			while (i < lines.length) {
				const fTrimmed = lines[i].trim();
				if (isClosingFence(fTrimmed, fenceLen)) break;
				inputLines.push(lines[i]);
				i++;
			}
			inputsJson = inputLines.join("\n");
			i++;
			continue;
		}
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		if (fenceOpen && fenceOpen.info !== "output") {
			contentLines.push(lines[i]);
			i++;
			while (i < lines.length) {
				contentLines.push(lines[i]);
				if (isMarkdownFenceClose(lines[i].trim(), fenceOpen)) {
					i++;
					break;
				}
				i++;
			}
			continue;
		}
		if (isForClose(trimmed)) {
			flushContent();
			const innerScope = {
				definitions: innerDefinitions,
				forDefinitions: innerForDefinitions,
				contentDefinitions: innerContentDefinitions,
				uses: innerUses.length > 0 ? innerUses : void 0,
				blocks: flattenTree(children)
			};
			const outputMode = attrs.output === "external" || attrs.output === "inline" ? attrs.output : void 0;
			const parsedForOutput = forOutputJson ? parseForOutput(forOutputJson) : void 0;
			const { externalRef: forExternalRef, output: forOutput } = extractExternalRef(parsedForOutput);
			const largeInlineOutputDiagnostic = createLargeInlineOutputDiagnostic({
				line: startIndex + 1,
				kind: "for",
				id: forId,
				output: parsedForOutput,
				outputMode,
				externalRef: forExternalRef
			});
			if (largeInlineOutputDiagnostic) diagnostics.push(largeInlineOutputDiagnostic);
			const parsedInputs = parseTemplateInputSchemaJson(inputsJson, startIndex + 1, `::for[${forId}]`);
			const inputSchema = parsedInputs.schema;
			diagnostics.push(...parsedInputs.diagnostics);
			const params = mergeTemplateParams(extractTemplateParams({
				of: attrs.of,
				definitions: [...innerDefinitions.values()].map((def) => def.config),
				contentDefinitions: [...innerContentDefinitions.values()].map((def) => def.body),
				uses: innerUses.map((use) => ({
					with: use.with,
					patch: use.patch,
					bind: use.bind
				})),
				forDefinitions: [...innerForDefinitions.values()].map((def) => ({
					of: def.of,
					params: def.params,
					innerScope: {
						definitions: [...def.innerScope.definitions.values()].map((inner) => inner.config),
						contentDefinitions: [...def.innerScope.contentDefinitions.values()].map((inner) => inner.body),
						uses: (def.innerScope.uses ?? []).map((use) => ({
							with: use.with,
							patch: use.patch,
							bind: use.bind
						}))
					}
				}))
			}), inputSchema);
			if (params.length > 0 && !isTemplate) diagnostics.push({
				line: startIndex + 1,
				severity: "warning",
				message: `::for[${forId}] uses $ref{input/...} but is not marked template="true"`,
				code: "for-missing-template"
			});
			const forDefinition = {
				id: forId,
				type: "for",
				each: attrs.each,
				of: attrs.of,
				...isTemplate ? { template: true } : {},
				...params.length > 0 ? { params } : {},
				limit: attrs.limit ? parseInt(attrs.limit) : void 0,
				offset: attrs.offset ? parseInt(attrs.offset) : void 0,
				concurrency: attrs.concurrency ? parseInt(attrs.concurrency) : void 0,
				innerScope,
				output: forOutput,
				...inputSchema ? { inputSchema } : {},
				outputMode,
				...forExternalRef ? { externalRef: forExternalRef } : {}
			};
			const node = {
				type: "container",
				directive: "for",
				attrs: {
					id: forId,
					...attrs
				},
				children
			};
			return {
				consumed: i - startIndex + 1,
				node,
				forDefinition,
				diagnostics: diagnostics.length > 0 ? diagnostics : void 0
			};
		}
		if (isForHeader(trimmed)) {
			flushContent();
			const nestedResult = parseForBlock(lines, i, isInsideInlineCode, isEscaped);
			if (nestedResult.node && nestedResult.forDefinition) {
				children.push(nestedResult.node);
				innerForDefinitions.set(nestedResult.forDefinition.id, nestedResult.forDefinition);
				registerInnerExecutableId(nestedResult.forDefinition.id, "for", i + 1);
			} else if (nestedResult.fallbackLines) contentLines.push(...nestedResult.fallbackLines);
			if (nestedResult.diagnostics) diagnostics.push(...nestedResult.diagnostics);
			i += nestedResult.consumed;
			continue;
		}
		if (isContentHeader(trimmed)) {
			flushContent();
			const contentResult = parseContentBlock(lines, i);
			if (contentResult.node?.definition) {
				const cd = contentResult.node.definition;
				innerContentDefinitions.set(cd.id, cd);
				children.push(contentResult.node);
				registerInnerExecutableId(cd.id, "content", i + 1);
			} else if (contentResult.fallbackLines) contentLines.push(...contentResult.fallbackLines);
			if (contentResult.diagnostics) diagnostics.push(...contentResult.diagnostics);
			i += contentResult.consumed;
			continue;
		}
		if (isDefineHeader(trimmed)) {
			flushContent();
			const defineResult = parseDefineBlock(lines, i);
			if (defineResult.node && defineResult.definition) {
				children.push(defineResult.node);
				innerDefinitions.set(defineResult.definition.id, defineResult.definition);
				registerInnerExecutableId(defineResult.definition.id, "definition", i + 1);
			} else if (defineResult.fallbackLines) contentLines.push(...defineResult.fallbackLines);
			if (defineResult.diagnostics) diagnostics.push(...defineResult.diagnostics);
			i += defineResult.consumed;
			continue;
		}
		if (isUseHeader(trimmed)) {
			flushContent();
			const useResult = parseUseBlock(lines, i);
			if (useResult.node?.binding) {
				innerUses.push(useResult.node.binding);
				children.push(useResult.node);
				registerInnerExecutableId(useResult.node.binding.id, "use", i + 1);
			} else if (useResult.fallbackLines) contentLines.push(...useResult.fallbackLines);
			if (useResult.diagnostics) diagnostics.push(...useResult.diagnostics);
			i += useResult.consumed;
			continue;
		}
		const forOutputMatch = trimmed.match(/^(`{3,})output\s*$/);
		if (forOutputMatch) {
			flushContent();
			const fenceLen = forOutputMatch[1].length;
			const outputLines = [];
			i++;
			while (i < lines.length) {
				const fTrimmed = lines[i].trim();
				if (isClosingFence(fTrimmed, fenceLen)) break;
				outputLines.push(lines[i]);
				i++;
			}
			forOutputJson = outputLines.join("\n");
			i++;
			continue;
		}
		contentLines.push(lines[i]);
		i++;
	}
	flushContent();
	const fallback = [headerLine];
	for (let j = startIndex + 1; j < lines.length; j++) fallback.push(lines[j]);
	return {
		consumed: lines.length - startIndex,
		fallbackLines: fallback
	};
}
function parseForOutput(json) {
	try {
		return JSON.parse(json);
	} catch {
		return;
	}
}
//#endregion
//#region src/parser/handlers/import-handler.ts
/**
* ::import 指令处理器
*
* 支持两种形式：
*
* 1. 单行指令（向后兼容）：
*    ::import{from="..." ns="..."}
*
* 2. 空块指令（与其他块级指令格式一致）：
*    ::import{from="..." ns="..."}
*    ::
*
* 3. 块指令（带 output 围栏）：
*    ::import{from="..." ns="..."}
*    ```output
*    {"ns/id": {...}, ...}
*    ```
*    ::
*
* 块形式使"导入即执行"——output 围栏缓存了导入定义的执行结果，
* 使得 ::render[ns/id]:: 无需手动 Run 即可展示。整篇导入文档快照
* 仅在 document="true" 时恢复，用于 ::render[ns]::。
*/
/**
* 判断是否是 ::import{...} 指令
*/
function isImportDirective(trimmed) {
	return IMPORT_RE.test(trimmed);
}
/**
* Parse an import binding from attrs string.
* Shared between single-line and block forms.
*/
function parseBinding(attrs, lineNum, diagnostics) {
	if (!attrs.from) diagnostics.push({
		line: lineNum,
		severity: "error",
		message: "::import is missing required attribute from",
		code: "import-missing-from"
	});
	if (!attrs.ns) diagnostics.push({
		line: lineNum,
		severity: "error",
		message: "::import is missing required attribute ns",
		code: "import-missing-ns"
	});
	if (attrs.pick && attrs.exclude) diagnostics.push({
		line: lineNum,
		severity: "error",
		message: "::import attributes pick and exclude are mutually exclusive and cannot be used together",
		code: "import-pick-exclude-conflict"
	});
	if (attrs.document !== void 0 && attrs.document !== "true" && attrs.document !== "false") diagnostics.push({
		line: lineNum,
		severity: "warning",
		message: "::import document attribute only supports \"true\" or \"false\"",
		code: "import-invalid-document-attr"
	});
	if (attrs.output !== void 0 && attrs.output !== "external" && attrs.output !== "inline") diagnostics.push({
		line: lineNum,
		severity: "warning",
		message: "::import output attribute only supports \"external\" or \"inline\"",
		code: "import-invalid-output-attr"
	});
	if (attrs.ns === "input") diagnostics.push({
		line: lineNum,
		severity: "error",
		message: "\"input\" is a reserved namespace and cannot be used as the ns value",
		code: "import-reserved-ns"
	});
	if (!attrs.from || !attrs.ns) return null;
	const pick = attrs.pick ? attrs.pick.split(",").map((s) => s.trim()).filter(Boolean) : void 0;
	const exclude = attrs.exclude ? attrs.exclude.split(",").map((s) => s.trim()).filter(Boolean) : void 0;
	let rename;
	if (attrs.rename) {
		rename = {};
		for (const pair of attrs.rename.split(",")) {
			const [from, to] = pair.split(":").map((s) => s.trim());
			if (from && to) rename[from] = to;
			else diagnostics.push({
				line: lineNum,
				severity: "warning",
				message: `::import rename format error: "${pair}", expected "oldName:newName"`,
				code: "import-invalid-rename"
			});
		}
	}
	return {
		source: attrs.from,
		namespace: attrs.ns,
		pick,
		exclude,
		rename: rename && Object.keys(rename).length > 0 ? rename : void 0,
		...attrs.document === "true" ? { document: true } : attrs.document === "false" ? { document: false } : {},
		line: lineNum,
		...attrs.output === "external" || attrs.output === "inline" ? { outputMode: attrs.output } : {}
	};
}
function stripImportDocumentSnapshot(output) {
	if (!output.document) return output;
	const next = { ...output };
	delete next.document;
	return next;
}
function applyImportDocumentPolicy(binding, output) {
	if (!output || binding.document) return output;
	return stripImportDocumentSnapshot(output);
}
/**
* 解析 ::import{...} 指令（单行或块形式）。
*
* 当传入 lines 数组时，支持向前看检测块形式（output 围栏 + :: 闭合）。
* 当只传入单行时，仅支持单行形式（向后兼容）。
*/
function parseImportDirective(lineOrLines, lineIndex) {
	const isSingleLineCall = typeof lineOrLines === "string";
	const line = isSingleLineCall ? lineOrLines : lineOrLines[lineIndex];
	const lines = isSingleLineCall ? void 0 : lineOrLines;
	const trimmed = line.trim();
	const match = IMPORT_RE.exec(trimmed);
	if (!match) return {
		consumed: 1,
		fallbackLines: [line]
	};
	const attrsStr = match[1];
	const attrs = parseAttrs(attrsStr);
	const diagnostics = [];
	const binding = parseBinding(attrs, lineIndex + 1, diagnostics);
	if (!binding) return {
		consumed: 1,
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0,
		fallbackLines: [line]
	};
	if (lines) {
		const blockResult = tryParseImportBlock(lines, lineIndex, binding, diagnostics);
		if (blockResult) return blockResult;
		const emptyBlockResult = tryParseEmptyImportBlock(lines, lineIndex, binding, diagnostics);
		if (emptyBlockResult) return emptyBlockResult;
	}
	return {
		consumed: 1,
		node: {
			type: "import",
			binding
		},
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0
	};
}
/**
* Try to parse an empty block-form import: header + ::
* Returns null when the next non-empty line is not a close marker.
*/
function tryParseEmptyImportBlock(lines, headerIndex, binding, diagnostics) {
	let cursor = headerIndex + 1;
	while (cursor < lines.length && lines[cursor].trim() === "") cursor++;
	if (cursor >= lines.length || !DEFINE_CLOSE_RE.test(lines[cursor].trim())) return null;
	return {
		consumed: cursor - headerIndex + 1,
		node: {
			type: "import",
			binding
		},
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0
	};
}
/**
* Try to parse a block-form import: header + ```output ... ``` + ::
* Returns null if next line is not an output fence (falls back to single-line).
*/
function tryParseImportBlock(lines, headerIndex, binding, diagnostics) {
	let cursor = headerIndex + 1;
	while (cursor < lines.length && lines[cursor].trim() === "") cursor++;
	if (cursor >= lines.length) return null;
	const fenceMatch = FENCE_OPEN_RE.exec(lines[cursor].trim());
	if (!fenceMatch || fenceMatch[2] !== "output") return null;
	const fenceLen = fenceMatch[1].length;
	const outputStart = cursor + 1;
	let outputEnd = outputStart;
	while (outputEnd < lines.length) {
		const fenceTrimmed = lines[outputEnd].trim();
		if (isClosingFence(fenceTrimmed, fenceLen)) break;
		outputEnd++;
	}
	if (outputEnd >= lines.length) {
		diagnostics.push({
			line: cursor + 1,
			severity: "warning",
			message: "::import output fence is not closed",
			code: "import-unclosed-output"
		});
		return null;
	}
	const outputText = lines.slice(outputStart, outputEnd).join("\n").trim();
	let output;
	let externalRef;
	if (outputText) try {
		const raw = JSON.parse(outputText);
		if (isExternalStub(raw)) externalRef = raw.__external;
		else if (raw && Array.isArray(raw.symbols)) {
			const largeInlineOutputDiagnostic = createLargeInlineOutputDiagnostic({
				line: headerIndex + 1,
				kind: "import",
				id: binding.namespace,
				output: raw,
				outputMode: binding.outputMode
			});
			if (largeInlineOutputDiagnostic) diagnostics.push(largeInlineOutputDiagnostic);
			const next = raw;
			output = {
				...next,
				source: next.source ?? binding.source,
				importedIds: next.importedIds ?? {},
				allExports: next.allExports ?? [],
				instanceSymbols: next.instanceSymbols ?? [],
				instanceOutputs: next.instanceOutputs ?? {}
			};
			output = applyImportDocumentPolicy(binding, output);
		} else diagnostics.push({
			line: outputStart + 1,
			severity: "warning",
			message: "::import output uses a legacy cache format; rerun this import",
			code: "import-legacy-output-unsupported"
		});
	} catch {
		diagnostics.push({
			line: outputStart + 1,
			severity: "warning",
			message: "::import output JSON parse failed",
			code: "import-invalid-output-json"
		});
	}
	cursor = outputEnd + 1;
	while (cursor < lines.length && lines[cursor].trim() === "") cursor++;
	if (cursor < lines.length && DEFINE_CLOSE_RE.test(lines[cursor].trim())) return {
		consumed: cursor - headerIndex + 1,
		node: {
			type: "import",
			binding: {
				...binding,
				...output ? { output } : {},
				...externalRef ? { externalRef } : {}
			}
		},
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0
	};
	diagnostics.push({
		line: headerIndex + 1,
		severity: "warning",
		message: "::import block form is missing the closing :: marker",
		code: "import-unclosed-block"
	});
	const finalBinding = {
		...binding,
		...output ? { output } : {},
		...externalRef ? { externalRef } : {}
	};
	return {
		consumed: cursor - headerIndex,
		node: {
			type: "import",
			binding: finalBinding
		},
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0
	};
}
//#endregion
//#region src/parser/handlers/meta-handler.ts
/**
* ::meta 块处理器
*
* 解析 ::meta[domain] 块：
*   meta-header → config-fence → close
*
* 支持域：module | config（未来可扩展）
*/
/**
* 判断是否是 ::meta[domain] 头部
*/
function isMetaHeader(trimmed) {
	return META_HEADER_RE.test(trimmed);
}
/**
* 从行数组的 startIndex 位置开始，解析一个 ::meta 块。
*/
function parseMetaBlock(lines, startIndex) {
	const headerLine = lines[startIndex];
	const trimmedHeader = headerLine.trim();
	const headerMatch = META_HEADER_RE.exec(trimmedHeader);
	if (!headerMatch) return {
		consumed: 1,
		fallbackLines: [headerLine]
	};
	const domain = headerMatch[1];
	const diagnostics = [];
	if (domain !== "module" && domain !== "config" && domain !== "output" && domain !== "snapshot") diagnostics.push({
		line: startIndex + 1,
		severity: "warning",
		message: `Unknown meta domain "${domain}"; known domains: module, config, output, snapshot`,
		code: "meta-unknown-domain"
	});
	let configLines = [];
	let inFence = false;
	let fenceLength = 0;
	let i = startIndex + 1;
	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();
		if (inFence) {
			if (isClosingFence(trimmed, fenceLength)) {
				inFence = false;
				i++;
				continue;
			}
			configLines.push(line);
			i++;
			continue;
		}
		if (/^::\s*$/.test(trimmed)) {
			const configText = configLines.join("\n").trim();
			let config = {};
			if (configText) try {
				const parsedConfig = JSON.parse(configText);
				if (typeof parsedConfig !== "object" || parsedConfig === null || Array.isArray(parsedConfig)) throw new Error("config root must be a JSON object");
				config = parsedConfig;
			} catch (e) {
				diagnostics.push({
					line: startIndex + 1,
					severity: "error",
					message: `::meta[${domain}] config JSON parse failed: ${e.message}`,
					code: "meta-invalid-json"
				});
			}
			if (domain === "config" && config.mode !== void 0 && config.mode !== "external" && config.mode !== "inline") diagnostics.push({
				line: startIndex + 1,
				severity: "error",
				message: "::meta[config].mode must be \"external\" or \"inline\"",
				code: "meta-config-invalid-mode"
			});
			return {
				consumed: i - startIndex + 1,
				node: {
					type: "meta",
					domain,
					config,
					range: {
						start: startIndex + 1,
						end: i + 1
					}
				},
				diagnostics: diagnostics.length > 0 ? diagnostics : void 0
			};
		}
		const fenceMatch = FENCE_OPEN_RE.exec(trimmed);
		if (fenceMatch && fenceMatch[2] === "config") {
			inFence = true;
			fenceLength = fenceMatch[1].length;
			configLines = [];
			i++;
			continue;
		}
		if (trimmed) diagnostics.push({
			line: i + 1,
			severity: "warning",
			message: `::meta block should not contain non-config content: "${trimmed}"`,
			code: "meta-unexpected-content"
		});
		i++;
	}
	diagnostics.push({
		line: startIndex + 1,
		severity: "error",
		message: `::meta[${domain}] block is not closed; missing ::`,
		code: "meta-unclosed"
	});
	return {
		consumed: lines.length - startIndex,
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0,
		fallbackLines: lines.slice(startIndex, lines.length)
	};
}
//#endregion
//#region src/parser/mdast-parser.ts
const containerHandlers = /* @__PURE__ */ new Map();
const CONTAINER_HEADER_RE = /^:::([A-Za-z][\w-]*)(?:\[([^\]]*)\])?(?:\{([^}]*)\})?\s*$/;
const CONTAINER_CLOSE_RE = /^:::\s*$/;
/** Register a standard container directive handler (`:::name ... :::`). */
function registerContainerHandler(name, handler) {
	containerHandlers.set(name, handler);
	return () => {
		if (containerHandlers.get(name) === handler) containerHandlers.delete(name);
	};
}
function parseMagicMd(source) {
	const definitions = /* @__PURE__ */ new Map();
	const forDefinitions = /* @__PURE__ */ new Map();
	const diagnostics = [];
	const imports = [];
	const uses = [];
	const contentDefinitions = /* @__PURE__ */ new Map();
	let meta;
	const tree = scanLines(source, definitions, forDefinitions, diagnostics, imports, uses, contentDefinitions, (m) => {
		meta = m;
	});
	return {
		blocks: flattenTree(tree),
		definitions,
		forDefinitions,
		tree,
		mdast: buildMdast(source),
		diagnostics: diagnostics.length > 0 ? diagnostics : void 0,
		meta,
		imports: imports.length > 0 ? imports : void 0,
		uses: uses.length > 0 ? uses : void 0,
		contentDefinitions: contentDefinitions.size > 0 ? contentDefinitions : void 0
	};
}
function scanLines(source, definitions, forDefinitions, diagnostics, imports, uses, contentDefinitions, setMeta) {
	const lines = source.split(/\r?\n/);
	const lineStarts = computeLineStarts(source);
	const toRange = createOffsetRangeMapper(source, lineStarts);
	const nodes = [];
	let contentLines = [];
	let contentLineIndexes = [];
	let contentStartLine = null;
	let currentMeta;
	const seenExecutableIds = /* @__PURE__ */ new Map();
	const registerExecutableId = (id, kind, line) => {
		const first = seenExecutableIds.get(id);
		if (!first) {
			seenExecutableIds.set(id, {
				kind,
				line
			});
			return;
		}
		const duplicateNamespace = kind === "import" && first.kind === "import";
		diagnostics.push({
			line,
			severity: "error",
			message: duplicateNamespace ? `Duplicate import namespace "${id}"; first declared on line ${first.line}` : `Duplicate executable ID "${id}"; first declared as ${first.kind} on line ${first.line}`,
			code: duplicateNamespace ? "duplicate-namespace" : "duplicate-definition"
		});
	};
	const pushContentLine = (lineIndex, value = lines[lineIndex]) => {
		if (contentStartLine == null) contentStartLine = lineIndex;
		contentLines.push(value);
		contentLineIndexes.push(lineIndex);
	};
	const pushFallbackLines = (fallbackLines, startLine) => {
		if (fallbackLines.length === 0) return;
		if (contentStartLine == null) contentStartLine = startLine;
		contentLines.push(...fallbackLines);
		for (let offset = 0; offset < fallbackLines.length; offset++) contentLineIndexes.push(startLine + offset);
	};
	const flushContent = () => {
		const text = contentLines.join("\n");
		if (text.trim()) {
			const startOffset = contentStartLine != null ? lineStarts[contentStartLine] ?? 0 : 0;
			const contentNodes = parseContent(text, isInsideInlineCode, isEscaped, {
				startOffset,
				toRange,
				toSourceOffset: createNormalizedContentOffsetMapper(text, contentLineIndexes, lineStarts, source.length)
			});
			nodes.push(...contentNodes);
		}
		contentLines = [];
		contentLineIndexes = [];
		contentStartLine = null;
	};
	let i = 0;
	while (i < lines.length) {
		const trimmed = lines[i].trim();
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		if (fenceOpen) {
			pushContentLine(i);
			i++;
			while (i < lines.length) {
				pushContentLine(i);
				if (isMarkdownFenceClose(lines[i].trim(), fenceOpen)) {
					i++;
					break;
				}
				i++;
			}
			continue;
		}
		const containerResult = parseRegisteredContainerAt(lines, i, definitions, diagnostics, 0);
		if (containerResult) {
			flushContent();
			if (containerResult.nodes) nodes.push(...containerResult.nodes);
			if (containerResult.fallbackLines) pushFallbackLines(containerResult.fallbackLines, i);
			i += containerResult.consumed;
			continue;
		}
		if (isMetaHeader(trimmed)) {
			flushContent();
			const result = parseMetaBlock(lines, i);
			if (result.node) {
				nodes.push(result.node);
				if (!currentMeta) currentMeta = {};
				if (result.node.domain === "module") {
					if (currentMeta.module) diagnostics.push({
						line: i + 1,
						severity: "error",
						message: "Duplicate ::meta[module]; each document may only contain one",
						code: "meta-duplicate-module"
					});
					else currentMeta.module = { exports: result.node.config.exports ?? [] };
				} else if (result.node.domain === "config") currentMeta.config = result.node.config;
				else if (result.node.domain === "output") currentMeta.output = result.node.config;
				else if (result.node.domain === "snapshot") currentMeta.snapshot = result.node.config;
				setMeta(currentMeta);
			} else if (result.fallbackLines) pushFallbackLines(result.fallbackLines, i);
			if (result.diagnostics) diagnostics.push(...result.diagnostics);
			i += result.consumed;
			continue;
		}
		if (isImportDirective(trimmed)) {
			flushContent();
			const result = parseImportDirective(lines, i);
			if (result.node) {
				nodes.push(result.node);
				imports.push(result.node.binding);
				registerExecutableId(result.node.binding.namespace, "import", i + 1);
			} else if (result.fallbackLines) pushFallbackLines(result.fallbackLines, i);
			if (result.diagnostics) diagnostics.push(...result.diagnostics);
			i += result.consumed;
			continue;
		}
		if (isUseHeader(trimmed)) {
			flushContent();
			const result = parseUseBlock(lines, i);
			if (result.node) {
				nodes.push(result.node);
				uses.push(result.node.binding);
				registerExecutableId(result.node.binding.id, "use", i + 1);
			} else if (result.fallbackLines) pushFallbackLines(result.fallbackLines, i);
			if (result.diagnostics) diagnostics.push(...result.diagnostics);
			i += result.consumed;
			continue;
		}
		if (isForHeader(trimmed)) {
			flushContent();
			const result = parseForBlock(lines, i, isInsideInlineCode, isEscaped);
			if (result.node && result.forDefinition) {
				nodes.push(result.node);
				registerForDefinitions(result.forDefinition, forDefinitions);
				registerExecutableId(result.forDefinition.id, "for", i + 1);
			} else if (result.fallbackLines) pushFallbackLines(result.fallbackLines, i);
			if (result.diagnostics) diagnostics.push(...result.diagnostics);
			i += result.consumed;
			continue;
		}
		if (isContentHeader(trimmed)) {
			flushContent();
			const result = parseContentBlock(lines, i);
			if (result.node) {
				nodes.push(result.node);
				contentDefinitions.set(result.node.definition.id, result.node.definition);
				registerExecutableId(result.node.definition.id, "content", i + 1);
			} else if (result.fallbackLines) pushFallbackLines(result.fallbackLines, i);
			if (result.diagnostics) diagnostics.push(...result.diagnostics);
			i += result.consumed;
			continue;
		}
		if (isDefineHeader(trimmed)) {
			flushContent();
			const result = parseDefineBlock(lines, i);
			if (result.node && result.definition) {
				definitions.set(result.definition.id, result.definition);
				nodes.push(result.node);
				registerExecutableId(result.definition.id, "definition", i + 1);
			} else if (result.fallbackLines) pushFallbackLines(result.fallbackLines, i);
			if (result.diagnostics) diagnostics.push(...result.diagnostics);
			i += result.consumed;
			continue;
		}
		pushContentLine(i);
		i++;
	}
	flushContent();
	return nodes;
}
function parseRegisteredContainerAt(lines, startIndex, definitions, diagnostics, lineOffset) {
	const header = CONTAINER_HEADER_RE.exec(lines[startIndex]?.trim() ?? "");
	if (!header) return null;
	const handler = containerHandlers.get(header[1]);
	if (!handler) return null;
	let depth = 1;
	let cursor = startIndex + 1;
	let activeFence = null;
	while (cursor < lines.length) {
		const current = lines[cursor].trim();
		if (activeFence) {
			if (isMarkdownFenceClose(current, activeFence)) activeFence = null;
			cursor++;
			continue;
		}
		const fenceOpen = parseMarkdownFenceOpen(current);
		if (fenceOpen) {
			activeFence = fenceOpen;
			cursor++;
			continue;
		}
		if (CONTAINER_HEADER_RE.test(current)) depth++;
		else if (CONTAINER_CLOSE_RE.test(current)) {
			depth--;
			if (depth === 0) break;
		}
		cursor++;
	}
	if (depth !== 0) {
		diagnostics.push({
			line: lineOffset + startIndex + 1,
			severity: "warning",
			message: `Unclosed :::${header[1]} container`,
			code: "container-unclosed"
		});
		return {
			consumed: lines.length - startIndex,
			fallbackLines: lines.slice(startIndex)
		};
	}
	const childLines = lines.slice(startIndex + 1, cursor);
	const children = parseRegisteredContainerChildren(childLines, definitions, diagnostics, lineOffset + startIndex + 1);
	try {
		return {
			consumed: cursor - startIndex + 1,
			nodes: handler(header[1], parseAttrs(header[3]), childLines, definitions, {
				...header[2] !== void 0 ? { label: header[2] } : {},
				children
			})
		};
	} catch (error) {
		diagnostics.push({
			line: lineOffset + startIndex + 1,
			severity: "error",
			message: `:::${header[1]} handler failed: ${error instanceof Error ? error.message : String(error)}`,
			code: "container-handler-failed"
		});
		return {
			consumed: cursor - startIndex + 1,
			fallbackLines: lines.slice(startIndex, cursor + 1)
		};
	}
}
function parseRegisteredContainerChildren(lines, definitions, diagnostics, lineOffset) {
	const nodes = [];
	let markdownLines = [];
	const flushMarkdown = () => {
		const text = markdownLines.join("\n");
		if (text.trim()) nodes.push(...parseContent(text, isInsideInlineCode, isEscaped));
		markdownLines = [];
	};
	let cursor = 0;
	while (cursor < lines.length) {
		const nested = parseRegisteredContainerAt(lines, cursor, definitions, diagnostics, lineOffset);
		if (!nested) {
			markdownLines.push(lines[cursor]);
			cursor++;
			continue;
		}
		flushMarkdown();
		if (nested.nodes) nodes.push(...nested.nodes);
		if (nested.fallbackLines) markdownLines.push(...nested.fallbackLines);
		cursor += nested.consumed;
	}
	flushMarkdown();
	return nodes;
}
function createNormalizedContentOffsetMapper(normalizedContent, sourceLineIndexes, sourceLineStarts, sourceLength) {
	const localLineStarts = computeLineStarts(normalizedContent);
	return (rawOffset) => {
		const offset = Math.max(0, Math.min(rawOffset, normalizedContent.length));
		let low = 0;
		let high = localLineStarts.length - 1;
		while (low <= high) {
			const mid = Math.floor((low + high) / 2);
			const start = localLineStarts[mid] ?? 0;
			const next = localLineStarts[mid + 1] ?? Number.POSITIVE_INFINITY;
			if (offset < start) high = mid - 1;
			else if (offset >= next) low = mid + 1;
			else {
				const sourceLine = sourceLineIndexes[mid] ?? sourceLineIndexes[0] ?? 0;
				return Math.min((sourceLineStarts[sourceLine] ?? sourceLength) + (offset - start), sourceLength);
			}
		}
		return sourceLength;
	};
}
/**
* 递归注册 ForDefinition 到 forDefinitions Map。
* 嵌套 for 通过遍历 Container children 收集。
*/
function registerForDefinitions(forDef, forDefinitions) {
	forDefinitions.set(forDef.id, forDef);
}
function buildMdast(source) {
	return fromMarkdown(source, {
		extensions: [directive()],
		mdastExtensions: [directiveFromMarkdown()]
	});
}
function computeLineStarts(source) {
	const starts = [0];
	for (let i = 0; i < source.length; i++) {
		if (source[i] !== "\n") continue;
		starts.push(i + 1);
	}
	return starts;
}
function createOffsetRangeMapper(source, lineStarts) {
	const offsetToPoint = (rawOffset) => {
		const offset = Math.max(0, Math.min(rawOffset, source.length));
		let low = 0;
		let high = lineStarts.length - 1;
		while (low <= high) {
			const mid = Math.floor((low + high) / 2);
			const start = lineStarts[mid] ?? 0;
			const nextStart = lineStarts[mid + 1] ?? Number.POSITIVE_INFINITY;
			if (offset < start) high = mid - 1;
			else if (offset >= nextStart) low = mid + 1;
			else return {
				line: mid + 1,
				column: offset - start
			};
		}
		const lastLine = Math.max(0, lineStarts.length - 1);
		return {
			line: lastLine + 1,
			column: offset - (lineStarts[lastLine] ?? 0)
		};
	};
	return (startOffset, endOffset) => {
		const start = offsetToPoint(startOffset);
		const end = offsetToPoint(endOffset);
		return {
			startLine: start.line,
			startColumn: start.column,
			endLine: end.line,
			endColumn: end.column
		};
	};
}
//#endregion
//#region src/unit-ref.ts
const UNIT_ID_RE = /^[\w-]+(?:\/[\w-]+)*$/;
const UNIT_REF_PREFIX = "unit/";
function normalizeUnitIdRef(value) {
	const trimmed = value.trim();
	const match = trimmed.match(REF_FULL_RE);
	if (!match) return trimmed;
	const refPath = match[1];
	if (!refPath.startsWith(UNIT_REF_PREFIX)) return trimmed;
	const unitId = refPath.slice(5);
	return UNIT_ID_RE.test(unitId) ? unitId : trimmed;
}
function formatUnitIdRef(unitId) {
	const normalized = normalizeUnitIdRef(unitId).trim();
	if (!normalized) return "";
	return UNIT_ID_RE.test(normalized) ? `$ref{unit/${normalized}}` : normalized;
}
function isUnitIdRef(value) {
	if (typeof value !== "string") return false;
	const match = value.trim().match(REF_FULL_RE);
	if (!match || !match[1].startsWith(UNIT_REF_PREFIX)) return false;
	return UNIT_ID_RE.test(match[1].slice(5));
}
//#endregion
//#region src/content-member-path.ts
const PATH_ID_RE = /^[\w-]+(?:\/[\w-]+)*$/;
function isValidScopedMemberCandidate(path) {
	return PATH_ID_RE.test(path);
}
function splitScopedMemberPath(targetId, hasRoot, hasRealUnit) {
	if (!targetId.includes("/") || !isValidScopedMemberCandidate(targetId)) return null;
	if (hasRealUnit?.(targetId)) return null;
	const parts = targetId.split("/");
	for (let i = parts.length - 1; i >= 1; i--) {
		const rootId = parts.slice(0, i).join("/");
		if (!hasRoot(rootId)) continue;
		return {
			rootId,
			memberPath: parts.slice(i)
		};
	}
	return null;
}
function resolveDependencyRootId(refId, hasUnit) {
	if (hasUnit(refId)) return refId;
	return splitScopedMemberPath(refId, hasUnit)?.rootId ?? null;
}
function getContentMemberValue(contentOutput, memberPath) {
	let current = contentOutput;
	for (const member of memberPath) {
		if (!current || typeof current !== "object" || Array.isArray(current)) return;
		current = current[member];
	}
	return current;
}
//#endregion
//#region src/resolver/ReferenceResolver.ts
const UNIT_IDENTITY_REF_PREFIX = "unit/";
const UNIT_IDENTITY_RE = /^[\w-]+(?:\/[\w-]+)*$/;
/**
* Compatibility lookup for cached import closures.
*
* Import execution stores non-exported instance symbols under
* `ns/__import__/localId`. Older documents can still contain refs written as
* `ns/localId`; when no public symbol exists, resolve them to the private
* closure entry without exposing that private ID in pickers/completion.
*/
function resolveImportPrivateAliasId(definitionId, hasDefinition) {
	if (definitionId.includes("/__import__/")) return null;
	const slashIndex = definitionId.indexOf("/");
	if (slashIndex <= 0 || slashIndex === definitionId.length - 1) return null;
	const privateId = `${definitionId.slice(0, slashIndex)}/__import__/${definitionId.slice(slashIndex + 1)}`;
	return hasDefinition(privateId) ? privateId : null;
}
/**
* 引用解析器
* 解析 $ref{definitionId.output.path.to.field} 和 $ref{contentId.body} 格式的引用
*/
var ReferenceResolver = class {
	/**
	* 解析配置中的所有引用
	* @param currentDefinitionId 当前正在执行的 definition ID，用于跳过自引用
	* @param getContentDefinition 可选的 content definition 查找函数（支持 $ref{id.body}）
	*/
	resolveConfig(config, getDefinition, currentDefinitionId, getContentDefinition, options) {
		return this.resolveValue(config, getDefinition, currentDefinitionId, getContentDefinition, options);
	}
	/**
	* 递归解析值中的引用
	*/
	resolveValue(value, getDefinition, currentDefinitionId, getContentDefinition, options) {
		if (typeof value === "string") return this.resolveString(value, getDefinition, currentDefinitionId, getContentDefinition, options);
		if (Array.isArray(value)) return value.map((item) => this.resolveValue(item, getDefinition, currentDefinitionId, getContentDefinition, options));
		if (value && typeof value === "object") {
			const result = {};
			for (const [key, val] of Object.entries(value)) result[key] = this.resolveValue(val, getDefinition, currentDefinitionId, getContentDefinition, options);
			return result;
		}
		return value;
	}
	/**
	* 解析字符串中的引用
	*/
	resolveString(str, getDefinition, currentDefinitionId, getContentDefinition, options) {
		const fullMatch = str.match(REF_FULL_RE);
		if (fullMatch) return this.resolveRef(fullMatch[1], getDefinition, currentDefinitionId, getContentDefinition, options);
		REF_RE.lastIndex = 0;
		return str.replace(REF_RE, (_, refPath) => {
			const value = this.resolveRef(refPath, getDefinition, currentDefinitionId, getContentDefinition, options);
			if (value === void 0) return "";
			return typeof value === "string" ? value : JSON.stringify(value);
		});
	}
	/**
	* 解析单个引用路径
	*/
	resolveRef(refPath, getDefinition, currentDefinitionId, getContentDefinition, options) {
		const assetRef = parseAssetRefPath(refPath);
		if (assetRef) return options?.resolveAssetRef?.(assetRef.relativePath, assetRef.namespace);
		const parts = this.parsePath(refPath);
		if (parts.length === 0) return void 0;
		const hasDefinition = (id) => !!getDefinition(id) || !!getContentDefinition?.(id);
		const hasUnitRef = options?.hasUnitRef ?? hasDefinition;
		const unitIdentityId = this.parseUnitIdentityRef(refPath);
		if (unitIdentityId) {
			let resolvedId = unitIdentityId;
			if (!hasUnitRef(resolvedId)) {
				const privateAlias = resolveImportPrivateAliasId(unitIdentityId, hasUnitRef);
				if (privateAlias) resolvedId = privateAlias;
			}
			if (!hasUnitRef(resolvedId)) return void 0;
			if (currentDefinitionId && resolvedId === currentDefinitionId) return;
			return resolvedId;
		}
		const scopedMember = this.resolveScopedContentMemberRef(parts, hasDefinition, getContentDefinition);
		if (scopedMember.resolved) {
			if (currentDefinitionId && scopedMember.rootId === currentDefinitionId) return;
			return scopedMember.value;
		}
		let definitionId = parts[0];
		let restIndex = 1;
		let unresolvedDefinitionId = definitionId;
		let unresolvedRestIndex = restIndex;
		{
			let candidate = parts[0];
			for (let i = 1; i < parts.length; i++) {
				const part = parts[i];
				if (part === "output" || part === "body" || part === "content") {
					unresolvedDefinitionId = candidate;
					unresolvedRestIndex = i;
					break;
				}
				candidate = `${candidate}/${part}`;
				if (hasDefinition(candidate)) {
					definitionId = candidate;
					restIndex = i + 1;
				}
			}
		}
		if (!hasDefinition(definitionId)) {
			const privateAlias = resolveImportPrivateAliasId(unresolvedDefinitionId, hasDefinition);
			if (privateAlias) {
				definitionId = privateAlias;
				restIndex = unresolvedRestIndex;
			}
		}
		if (currentDefinitionId && definitionId === currentDefinitionId) return;
		const accessor = parts[restIndex];
		if (accessor === "body" || accessor === "content") {
			const contentDef = getContentDefinition?.(definitionId);
			if (!contentDef) return void 0;
			return contentDef.body;
		}
		if (accessor !== "output") return;
		if (parts[restIndex + 1] === "__formula_dep__") return definitionId;
		const definition = getDefinition(definitionId);
		if (definition?.output) return this.getNestedValue(definition.output, parts.slice(restIndex + 1));
		const contentDef = getContentDefinition?.(definitionId);
		if (contentDef?.output) return this.getNestedValue(contentDef.output, parts.slice(restIndex + 1));
	}
	resolveScopedContentMemberRef(parts, hasDefinition, getContentDefinition) {
		if (!getContentDefinition) return { resolved: false };
		const accessorIndex = parts.findIndex((part) => part === "output" || part === "body" || part === "content");
		if (accessorIndex <= 0) return { resolved: false };
		const targetId = parts.slice(0, accessorIndex).join("/");
		if (hasDefinition(targetId)) return { resolved: false };
		const scoped = splitScopedMemberPath(targetId, (id) => !!getContentDefinition(id), hasDefinition);
		if (!scoped) return { resolved: false };
		const accessor = parts[accessorIndex];
		if (accessor === "body" || accessor === "content") {
			const memberContent = findNestedContentDefinition(getContentDefinition(scoped.rootId), scoped.memberPath);
			return {
				resolved: true,
				rootId: scoped.rootId,
				value: memberContent?.body
			};
		}
		if (accessor !== "output") return {
			resolved: true,
			rootId: scoped.rootId,
			value: void 0
		};
		const memberOutput = getContentMemberValue(getContentDefinition(scoped.rootId)?.output, scoped.memberPath);
		if (memberOutput === void 0) return {
			resolved: true,
			rootId: scoped.rootId,
			value: void 0
		};
		return {
			resolved: true,
			rootId: scoped.rootId,
			value: this.getNestedValue(memberOutput, parts.slice(accessorIndex + 1))
		};
	}
	/**
	* 解析路径字符串
	* 支持 ns/id 格式：/ 作为分隔符（与 . 同级）分割路径段
	*/
	parsePath(path) {
		const parts = [];
		const pattern = /([\w-]+)|\[(\d+)\]/g;
		let match;
		while ((match = pattern.exec(path)) !== null) parts.push(match[1] || match[2]);
		return parts;
	}
	parseUnitIdentityRef(path) {
		if (!path.startsWith(UNIT_IDENTITY_REF_PREFIX)) return null;
		const unitId = path.slice(5);
		return UNIT_IDENTITY_RE.test(unitId) ? unitId : null;
	}
	/**
	* 获取嵌套值
	*/
	getNestedValue(obj, path) {
		let current = obj;
		for (const key of path) {
			if (current === null || current === void 0) return;
			if (Array.isArray(current)) current = current[parseInt(key, 10)];
			else if (typeof current === "object") current = current[key];
			else return;
		}
		return current;
	}
};
function findNestedContentDefinition(root, memberPath) {
	let current = root;
	for (const member of memberPath) {
		if (!current) return null;
		let parsed;
		try {
			parsed = parseMagicMd(current.body);
		} catch {
			return null;
		}
		current = parsed.contentDefinitions?.get(member);
	}
	return current ?? null;
}
//#endregion
//#region src/serializer/OutputSerializer.ts
function splitDocumentLines(content) {
	const lines = [];
	const separators = [];
	const counts = {
		"\n": 0,
		"\r\n": 0
	};
	let start = 0;
	const re = /\r\n|\n/g;
	let match;
	while ((match = re.exec(content)) !== null) {
		lines.push(content.slice(start, match.index));
		separators.push(match[0]);
		counts[match[0]]++;
		start = match.index + match[0].length;
	}
	lines.push(content.slice(start));
	separators.push("");
	return {
		lines,
		separators,
		defaultEol: counts["\r\n"] > counts["\n"] ? "\r\n" : "\n"
	};
}
function joinDocumentLines(document) {
	return document.lines.map((line, index) => line + (document.separators[index] ?? "")).join("");
}
function spliceDocumentLines(document, start, deleteCount, ...newLines) {
	const { lines, separators, defaultEol } = document;
	const boundedStart = Math.max(0, Math.min(start, lines.length));
	const boundedDelete = Math.max(0, Math.min(deleteCount, lines.length - boundedStart));
	if (boundedDelete === 0) {
		lines.splice(boundedStart, 0, ...newLines);
		separators.splice(boundedStart, 0, ...newLines.map(() => defaultEol));
		return;
	}
	const inheritedSeparator = separators[boundedStart + boundedDelete - 1] ?? (boundedStart > 0 ? separators[boundedStart - 1] : defaultEol);
	lines.splice(boundedStart, boundedDelete, ...newLines);
	const replacementSeparators = newLines.map((_line, index) => index === newLines.length - 1 ? inheritedSeparator : defaultEol);
	separators.splice(boundedStart, boundedDelete, ...replacementSeparators);
	if (newLines.length === 0 && boundedStart > 0) separators[boundedStart - 1] = inheritedSeparator;
}
function isLineInTargetRange(line, locator) {
	return line >= (locator?.startLine ?? 0) && line <= (locator?.endLine ?? Number.POSITIVE_INFINITY);
}
function createTargetSelector(locator, opaqueLines = /* @__PURE__ */ new Set()) {
	let occurrence = 0;
	return (line) => {
		if (opaqueLines.has(line) || !isLineInTargetRange(line, locator)) return false;
		return occurrence++ === (locator?.occurrence ?? 0);
	};
}
function collectMarkdownFencedLines(lines) {
	const opaque = /* @__PURE__ */ new Set();
	let activeFence = null;
	for (let line = 0; line < lines.length; line++) {
		const trimmed = lines[line].trim();
		if (activeFence) {
			opaque.add(line);
			if (isMarkdownFenceClose(trimmed, activeFence)) activeFence = null;
			continue;
		}
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		if (fenceOpen) {
			activeFence = fenceOpen;
			opaque.add(line);
		}
	}
	return opaque;
}
const BLOCK_OPENER_RE = /^::(define\[|content\[|meta\[|use\[)/;
function isBlockFormImport(lines, headerIndex) {
	return parseImportDirective(lines, headerIndex).consumed > 1;
}
function isBlockScopedDirective(lines, lineIndex) {
	const trimmed = lines[lineIndex].trim();
	if (BLOCK_OPENER_RE.test(trimmed)) return true;
	return IMPORT_RE.test(trimmed) && isBlockFormImport(lines, lineIndex);
}
function parseExecutableUnitId(trimmed) {
	const defineMatch = DEFINE_HEADER_RE.exec(trimmed);
	if (defineMatch) return defineMatch[1];
	const forMatch = FOR_HEADER_RE.exec(trimmed);
	if (forMatch) return forMatch[1];
	const contentMatch = CONTENT_HEADER_RE.exec(trimmed);
	if (contentMatch) return contentMatch[1];
	const useMatch = USE_HEADER_RE.exec(trimmed);
	if (useMatch) return useMatch[1];
	const importMatch = IMPORT_RE.exec(trimmed);
	if (!importMatch) return null;
	return parseAttrs(importMatch[1]).ns ?? null;
}
/**
* Collect executable IDs while treating Markdown fenced blocks as opaque.
*
* Output/config/input fences are Markdown fences too, so this also prevents a
* header-shaped string inside persisted JSON from making an otherwise unique
* target look ambiguous.
*/
function collectExecutableUnitIdCounts(lines, locator) {
	const counts = /* @__PURE__ */ new Map();
	let markdownFence = null;
	for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
		const trimmed = lines[lineIndex].trim();
		if (markdownFence) {
			if (isMarkdownFenceClose(trimmed, markdownFence)) markdownFence = null;
			continue;
		}
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		if (fenceOpen) {
			markdownFence = fenceOpen;
			continue;
		}
		const id = parseExecutableUnitId(trimmed);
		if (!id || !isLineInTargetRange(lineIndex, locator)) continue;
		counts.set(id, (counts.get(id) ?? 0) + 1);
	}
	return counts;
}
function collectAmbiguousExecutableIds(lines, locator) {
	const counts = collectExecutableUnitIdCounts(lines, locator);
	const ambiguousIds = /* @__PURE__ */ new Set();
	if (locator?.occurrence !== void 0) return ambiguousIds;
	for (const [id, count] of counts) if (count > 1) ambiguousIds.add(id);
	return ambiguousIds;
}
function refuseAmbiguousExecutableTarget(ambiguousIds, unitId, operation) {
	if (!ambiguousIds.has(unitId)) return false;
	console.warn(`[OutputSerializer] Refusing ${operation} for duplicate executable ID "${unitId}" in the same scope`);
	return true;
}
function getLeadingWhitespace(line) {
	return line.slice(0, line.length - line.trimStart().length);
}
function detectIndentUnit(indent) {
	return indent.includes("	") ? "	" : "    ";
}
/**
* Serializes plugin output back into the Markdown document.
*
* Given a Markdown string, finds the `::define[id]{...}` block and updates
* (or inserts) its fenced output section with the new output data.
*
* Supports dynamic fence lengths (3+ backticks) per CommonMark §4.5 to
* handle content containing backtick sequences.
*
* This keeps the document self-contained — all config and results live
* in the .md file itself (or reference __external files).
*/
var OutputSerializer = class {
	/**
	* Update the output block for a given definition ID in the markdown content.
	* If the define block already has an output section, replace it.
	* If not, insert one before the closing `::`.
	*
	* Uses line-by-line scanning to correctly handle dynamic fence lengths.
	*/
	updateOutput(content, definitionId, output, locator) {
		const outputJson = JSON.stringify(output);
		const fenceLen = computeFenceLength(outputJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), definitionId, "output update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		let i = 0;
		while (i < lines.length) {
			const hdrMatch = lines[i].trim().match(DEFINE_HEADER_RE);
			if (!hdrMatch || hdrMatch[1] !== definitionId || !selectTarget(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			let inFence = false;
			let fenceKind = "";
			let activeFenceLen = 3;
			let outputStart = -1;
			let outputEnd = -1;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (!inFence) {
					if (t === "::") break;
					const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
					if (fmatch) {
						inFence = true;
						fenceKind = fmatch[2];
						activeFenceLen = fmatch[1].length;
						if (fenceKind === "output") outputStart = j;
					}
				} else if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
					if (fenceKind === "output") outputEnd = j;
					inFence = false;
					fenceKind = "";
				}
				j++;
			}
			const blockEnd = j;
			if (outputStart >= 0 && outputEnd >= 0) {
				const newOutputLines = [
					`${fence}output`,
					outputJson,
					fence
				];
				spliceDocumentLines(document, outputStart, outputEnd - outputStart + 1, ...newOutputLines);
			} else if (blockEnd < lines.length) spliceDocumentLines(document, blockEnd, 0, ...[
				`${fence}output`,
				outputJson,
				fence
			]);
			else {
				console.warn(`[OutputSerializer] Definition "${definitionId}" block not closed`);
				return content;
			}
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] Definition "${definitionId}" not found in document`);
		return content;
	}
	/**
	* Batch update multiple definitions' outputs in a single split/join pass.
	*
	* More efficient than calling `updateOutput()` in a loop, because the
	* document string is split into lines only once and all matching define
	* blocks are updated in-place before re-joining.
	*/
	updateMultiple(content, outputs, locators) {
		if (outputs.size === 0) return content;
		const pending = /* @__PURE__ */ new Map();
		for (const [id, output] of outputs) {
			const json = JSON.stringify(output);
			const fLen = computeFenceLength(json);
			pending.set(id, {
				json,
				fenceLen: fLen,
				fence: makeFence(fLen)
			});
		}
		const document = splitDocumentLines(content);
		const { lines } = document;
		for (const id of [...pending.keys()]) if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locators?.get(id)), id, "batched output update")) pending.delete(id);
		if (pending.size === 0) return content;
		const selectors = new Map([...pending.keys()].map((id) => [id, createTargetSelector(locators?.get(id))]));
		let i = 0;
		let opaqueLines = collectMarkdownFencedLines(lines);
		while (i < lines.length && pending.size > 0) {
			if (opaqueLines.has(i)) {
				i++;
				continue;
			}
			const hdrMatch = lines[i].trim().match(DEFINE_HEADER_RE);
			if (!hdrMatch) {
				i++;
				continue;
			}
			const defId = hdrMatch[1];
			const entry = pending.get(defId);
			if (!entry || !selectors.get(defId)?.(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			let inFence = false;
			let fenceKind = "";
			let activeFenceLen = 3;
			let outputStart = -1;
			let outputEnd = -1;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (!inFence) {
					if (t === "::") break;
					const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
					if (fmatch) {
						inFence = true;
						fenceKind = fmatch[2];
						activeFenceLen = fmatch[1].length;
						if (fenceKind === "output") outputStart = j;
					}
				} else if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
					if (fenceKind === "output") outputEnd = j;
					inFence = false;
					fenceKind = "";
				}
				j++;
			}
			const blockEnd = j;
			const newOutputLines = [
				`${entry.fence}output`,
				entry.json,
				entry.fence
			];
			if (outputStart >= 0 && outputEnd >= 0) {
				spliceDocumentLines(document, outputStart, outputEnd - outputStart + 1, ...newOutputLines);
				i = outputStart + newOutputLines.length;
			} else if (blockEnd < lines.length) {
				spliceDocumentLines(document, blockEnd, 0, ...newOutputLines);
				i = blockEnd + newOutputLines.length + 1;
			} else {
				console.warn(`[OutputSerializer] Definition "${defId}" block not closed`);
				i = j;
			}
			opaqueLines = collectMarkdownFencedLines(lines);
			pending.delete(defId);
		}
		return joinDocumentLines(document);
	}
	/**
	* Update the config block for a given definition ID in the markdown content.
	* Replaces the config fenced block inside the define block with the new config.
	*
	* Supports dynamic fence lengths (3+ backticks).
	*/
	updateConfig(content, definitionId, config, locator) {
		const configJson = JSON.stringify(config);
		const fenceLen = computeFenceLength(configJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), definitionId, "config update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		let i = 0;
		while (i < lines.length) {
			const hdrMatch = lines[i].trim().match(DEFINE_HEADER_RE);
			if (!hdrMatch || hdrMatch[1] !== definitionId || !selectTarget(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			let inFence = false;
			let fenceKind = "";
			let activeFenceLen = 3;
			let configStart = -1;
			let configEnd = -1;
			let outputStart = -1;
			let blockEnd = -1;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (!inFence) {
					if (t === "::") {
						blockEnd = j;
						break;
					}
					const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
					if (fmatch) {
						inFence = true;
						fenceKind = fmatch[2];
						activeFenceLen = fmatch[1].length;
						if (fenceKind === "config") configStart = j;
						else if (outputStart < 0) outputStart = j;
					}
				} else if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
					if (fenceKind === "config") configEnd = j;
					inFence = false;
					fenceKind = "";
				}
				j++;
			}
			const newConfigLines = [
				`${fence}config`,
				configJson,
				fence
			];
			if (configStart >= 0 && configEnd >= 0) {
				spliceDocumentLines(document, configStart, configEnd - configStart + 1, ...newConfigLines);
				return joinDocumentLines(document);
			}
			if (blockEnd < lines.length) {
				spliceDocumentLines(document, outputStart >= 0 ? outputStart : blockEnd, 0, ...newConfigLines);
				return joinDocumentLines(document);
			}
			console.warn(`[OutputSerializer] Definition "${definitionId}" block not closed for config update`);
			return content;
		}
		console.warn(`[OutputSerializer] Definition "${definitionId}" not found for config update`);
		return content;
	}
	/**
	* Update the for-output block for a given ::for[id] block.
	* If the for block already has a for-output section, replace it.
	* If not, insert one before the closing ::endfor.
	*/
	updateForOutput(content, forId, output, locator) {
		const outputJson = JSON.stringify(output);
		const fenceLen = computeFenceLength(outputJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), forId, "for output update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		let i = 0;
		while (i < lines.length) {
			const trimmed = lines[i].trim();
			const headerMatch = FOR_HEADER_RE.exec(trimmed);
			if (!headerMatch || headerMatch[1] !== forId || !selectTarget(i)) {
				i++;
				continue;
			}
			const headerIndent = getLeadingWhitespace(lines[i]);
			let j = i + 1;
			let nestDepth = 1;
			let forOutputStart = -1;
			let forOutputEnd = -1;
			let inFence = false;
			let activeFenceLen = 3;
			let fenceKind = "";
			let capturesForOutputFence = false;
			let markdownFence = null;
			let innerBlockDepth = 0;
			let bodyIndent = null;
			while (j < lines.length && nestDepth > 0) {
				const t = lines[j].trim();
				if (bodyIndent === null && t !== "") {
					const lineIndent = getLeadingWhitespace(lines[j]);
					if (lineIndent.length > headerIndent.length) bodyIndent = lineIndent;
				}
				if (inFence) {
					if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
						if (capturesForOutputFence) forOutputEnd = j;
						inFence = false;
						fenceKind = "";
						capturesForOutputFence = false;
					}
					j++;
					continue;
				}
				if (markdownFence) {
					if (isMarkdownFenceClose(t, markdownFence)) markdownFence = null;
					j++;
					continue;
				}
				const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
				if (fmatch) {
					inFence = true;
					fenceKind = fmatch[2];
					activeFenceLen = fmatch[1].length;
					capturesForOutputFence = fenceKind === "output" && nestDepth === 1 && innerBlockDepth === 0;
					if (capturesForOutputFence) forOutputStart = j;
					j++;
					continue;
				}
				const markdownFenceOpen = parseMarkdownFenceOpen(t);
				if (markdownFenceOpen) {
					markdownFence = markdownFenceOpen;
					j++;
					continue;
				}
				if (FOR_HEADER_RE.test(t)) nestDepth++;
				else if (FOR_CLOSE_RE.test(t)) {
					nestDepth--;
					if (nestDepth === 0) break;
				} else if (nestDepth === 1 && isBlockScopedDirective(lines, j)) innerBlockDepth++;
				else if (t === "::" && nestDepth === 1 && innerBlockDepth > 0) innerBlockDepth--;
				j++;
			}
			const endforLine = j;
			const outputIndent = bodyIndent ?? `${headerIndent}${detectIndentUnit(headerIndent)}`;
			if (forOutputStart >= 0 && forOutputEnd >= 0) {
				const newLines = [
					`${outputIndent}${fence}output`,
					`${outputIndent}${outputJson}`,
					`${outputIndent}${fence}`
				];
				spliceDocumentLines(document, forOutputStart, forOutputEnd - forOutputStart + 1, ...newLines);
			} else if (endforLine < lines.length) spliceDocumentLines(document, endforLine, 0, ...[
				`${outputIndent}${fence}output`,
				`${outputIndent}${outputJson}`,
				`${outputIndent}${fence}`
			]);
			else {
				console.warn(`[OutputSerializer] For block "${forId}" not closed`);
				return content;
			}
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] For block "${forId}" not found`);
		return content;
	}
	/**
	* Update the output block for a ::use[id]{...} block.
	* If the use block already has an output section, replace it.
	* If not, insert one before the closing `::`.
	*/
	updateUseOutput(content, useId, output, locator) {
		const outputJson = JSON.stringify(output);
		const fenceLen = computeFenceLength(outputJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), useId, "use output update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		const useRe = new RegExp(`^::use\\[${this.escapeRegex(useId)}\\]\\{[^}]*\\}\\s*$`);
		let i = 0;
		while (i < lines.length) {
			if (!useRe.test(lines[i].trim()) || !selectTarget(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			let inFence = false;
			let fenceKind = "";
			let activeFenceLen = 3;
			let outputStart = -1;
			let outputEnd = -1;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (!inFence) {
					if (t === "::") break;
					const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
					if (fmatch) {
						inFence = true;
						fenceKind = fmatch[2];
						activeFenceLen = fmatch[1].length;
						if (fenceKind === "output") outputStart = j;
					}
				} else if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
					if (fenceKind === "output") outputEnd = j;
					inFence = false;
					fenceKind = "";
				}
				j++;
			}
			const blockEnd = j;
			if (outputStart >= 0 && outputEnd >= 0) {
				const newOutputLines = [
					`${fence}output`,
					outputJson,
					fence
				];
				spliceDocumentLines(document, outputStart, outputEnd - outputStart + 1, ...newOutputLines);
			} else if (blockEnd < lines.length) spliceDocumentLines(document, blockEnd, 0, ...[
				`${fence}output`,
				outputJson,
				fence
			]);
			else {
				console.warn(`[OutputSerializer] Use block "${useId}" not closed`);
				return content;
			}
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] Use block "${useId}" not found in document`);
		return content;
	}
	/**
	* Update the config block (patch/bind/with) for a ::use[id]{...} block.
	* If the use block already has a config section, replace it.
	* If not, insert one after the use header.
	*
	* @param content - Markdown document string
	* @param useId - The use block ID
	* @param patch - The patch fields to write
	* @param options - Optional bind and with fields
	*/
	updateUseConfig(content, useId, patch, options, locator) {
		const configObj = {};
		if (Object.keys(patch).length > 0) configObj.patch = patch;
		if (options?.bind && Object.keys(options.bind).length > 0) configObj.bind = options.bind;
		if (options?.with && Object.keys(options.with).length > 0) configObj.with = options.with;
		const configJson = JSON.stringify(configObj);
		const fenceLen = computeFenceLength(configJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), useId, "use config update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		const useRe = new RegExp(`^::use\\[${this.escapeRegex(useId)}\\]\\{[^}]*\\}\\s*$`);
		let i = 0;
		while (i < lines.length) {
			if (!useRe.test(lines[i].trim()) || !selectTarget(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			let inFence = false;
			let fenceKind = "";
			let activeFenceLen = 3;
			let configStart = -1;
			let configEnd = -1;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (!inFence) {
					if (t === "::") break;
					const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
					if (fmatch) {
						inFence = true;
						fenceKind = fmatch[2];
						activeFenceLen = fmatch[1].length;
						if (fenceKind === "config") configStart = j;
					}
				} else if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
					if (fenceKind === "config") configEnd = j;
					inFence = false;
					fenceKind = "";
				}
				j++;
			}
			const newConfigLines = [
				`${fence}config`,
				configJson,
				fence
			];
			if (configStart >= 0 && configEnd >= 0) spliceDocumentLines(document, configStart, configEnd - configStart + 1, ...newConfigLines);
			else spliceDocumentLines(document, i + 1, 0, ...newConfigLines);
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] Use block "${useId}" not found for config update`);
		return content;
	}
	/**
	* Update the output block for a ::content[id] block.
	* Output fence is placed before the closing `::`, after all body content.
	* Handles nesting depth to avoid matching inner `::` closes.
	*/
	updateContentOutput(content, contentId, output, locator) {
		const outputJson = JSON.stringify(output);
		const fenceLen = computeFenceLength(outputJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), contentId, "content output update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		const contentRe = new RegExp(`^::content\\[${this.escapeRegex(contentId)}\\](\\{[^}]*\\})?\\s*$`);
		/** Regex matching block openers that close with a standalone `::` line */
		let i = 0;
		while (i < lines.length) {
			if (!contentRe.test(lines[i].trim()) || !selectTarget(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			let nestingDepth = 0;
			let forNestingDepth = 0;
			let inFence = false;
			let fenceKind = "";
			let activeFenceLen = 3;
			let outputStart = -1;
			let outputEnd = -1;
			let capturesContentOutputFence = false;
			let markdownFence = null;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (inFence) {
					if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
						if (capturesContentOutputFence) outputEnd = j;
						inFence = false;
						fenceKind = "";
						capturesContentOutputFence = false;
					}
					j++;
					continue;
				}
				if (markdownFence) {
					if (isMarkdownFenceClose(t, markdownFence)) markdownFence = null;
					j++;
					continue;
				}
				const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
				if (fmatch) {
					inFence = true;
					fenceKind = fmatch[2];
					activeFenceLen = fmatch[1].length;
					capturesContentOutputFence = fenceKind === "output" && nestingDepth === 0 && forNestingDepth === 0;
					if (capturesContentOutputFence) outputStart = j;
					j++;
					continue;
				}
				const markdownFenceOpen = parseMarkdownFenceOpen(t);
				if (markdownFenceOpen) {
					markdownFence = markdownFenceOpen;
					j++;
					continue;
				}
				if (isBlockScopedDirective(lines, j)) nestingDepth++;
				else if (FOR_HEADER_RE.test(t)) forNestingDepth++;
				else if (FOR_CLOSE_RE.test(t)) {
					if (forNestingDepth > 0) forNestingDepth--;
				} else if (t === "::") {
					if (nestingDepth > 0) nestingDepth--;
					else if (forNestingDepth > 0) {} else break;
				}
				j++;
			}
			const blockEnd = j;
			if (outputStart >= 0 && outputEnd >= 0) {
				const newOutputLines = [
					`${fence}output`,
					outputJson,
					fence
				];
				spliceDocumentLines(document, outputStart, outputEnd - outputStart + 1, ...newOutputLines);
			} else if (blockEnd < lines.length) spliceDocumentLines(document, blockEnd, 0, ...[
				`${fence}output`,
				outputJson,
				fence
			]);
			else {
				console.warn(`[OutputSerializer] Content block "${contentId}" not closed`);
				return content;
			}
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] Content block "${contentId}" not found`);
		return content;
	}
	/**
	* Update the output block for a ::import{from="..." ns="..."} directive.
	*
	* Converts a single-line import to block form with output fence:
	*   ::import{from="..." ns="..."}
	*   ```output
	*   {"ns/id": {...}, ...}
	*   ```
	*   ::
	*
	* If the import already has a block form with output, replaces the output.
	* Uses the `namespace` to find the correct ::import directive.
	*
	* "Import = Run" — the output fence persists imported definition results.
	*/
	updateImportOutput(content, namespace, output, locator) {
		const outputJson = JSON.stringify(output);
		const fenceLen = computeFenceLength(outputJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), namespace, "import output update")) return content;
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		let i = 0;
		while (i < lines.length) {
			const trimmed = lines[i].trim();
			if (!IMPORT_RE.test(trimmed)) {
				i++;
				continue;
			}
			const nsMatch = trimmed.match(/ns="([^"]+)"/);
			if (!nsMatch || nsMatch[1] !== namespace) {
				i++;
				continue;
			}
			if (!selectTarget(i)) {
				i++;
				continue;
			}
			let j = i + 1;
			while (j < lines.length && lines[j].trim() === "") j++;
			let existingOutputStart = -1;
			let existingOutputEnd = -1;
			let existingBlockEnd = -1;
			if (j < lines.length) {
				if (lines[j].trim() === "::") existingBlockEnd = j;
				const fmatch = FENCE_OPEN_RE.exec(lines[j].trim());
				if (fmatch && fmatch[2] === "output") {
					existingOutputStart = j;
					const existingFenceLen = fmatch[1].length;
					let k = j + 1;
					while (k < lines.length) {
						const kt = lines[k].trim();
						if (isClosingFence(kt.replace(/\s*$/, ""), existingFenceLen)) {
							existingOutputEnd = k;
							break;
						}
						k++;
					}
					if (existingOutputEnd >= 0) {
						let m = existingOutputEnd + 1;
						while (m < lines.length && lines[m].trim() === "") m++;
						if (m < lines.length && lines[m].trim() === "::") existingBlockEnd = m;
					}
				}
			}
			if (existingOutputStart >= 0 && existingOutputEnd >= 0 && existingBlockEnd >= 0) {
				const newOutputLines = [
					`${fence}output`,
					outputJson,
					fence
				];
				spliceDocumentLines(document, existingOutputStart, existingOutputEnd - existingOutputStart + 1, ...newOutputLines);
			} else if (existingBlockEnd >= 0) spliceDocumentLines(document, existingBlockEnd, 0, `${fence}output`, outputJson, fence);
			else {
				const newLines = [
					`${fence}output`,
					outputJson,
					fence,
					"::"
				];
				spliceDocumentLines(document, i + 1, 0, ...newLines);
			}
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] Import with ns="${namespace}" not found`);
		return content;
	}
	escapeRegex(str) {
		return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	}
	/**
	* Update (insert / replace / remove) a single attribute on a unit header line.
	*
	* Supports: ::define[id]{...}, ::content[id]{...}, ::for[id]{...},
	*           ::use[ns/name]{...}, ::import{...}.
	*
	* - `value` is the new attribute value (no quotes). Pass `null` to remove.
	* - Preserves order of existing attrs; appends new attrs at end.
	* - For `::content[id]`, attributes block `{...}` is optional — will be
	*   inserted if missing.
	*
	* @param content   - Markdown document string
	* @param unitKind  - "define" | "content" | "for" | "use" | "import"
	* @param unitId    - For define/content/for/use: the bracket id; for import: the `from` value (unique key)
	* @param attrName  - Attribute key (e.g. "snapshot", "output")
	* @param value     - New value, or null to remove
	*/
	updateUnitAttr(content, unitKind, unitId, attrName, value, locator) {
		const document = splitDocumentLines(content);
		const { lines } = document;
		const matchLine = (line) => {
			const trimmed = line.trim();
			const indent = line.slice(0, line.length - line.trimStart().length);
			const matchBracketed = (kind) => {
				const re = new RegExp(`^::${kind}\\[([^\\]]+)\\]\\{(.*)\\}\\s*$`);
				const m = trimmed.match(re);
				return m ? {
					id: m[1],
					attrs: m[2]
				} : null;
			};
			if (unitKind === "define") {
				const m = matchBracketed("define");
				if (!m || m.id !== unitId) return null;
				return {
					attrsStr: m.attrs,
					rebuild: (a) => `${indent}::define[${m.id}]{${a}}`
				};
			}
			if (unitKind === "content") {
				const withAttrs = matchBracketed("content");
				if (withAttrs && withAttrs.id === unitId) return {
					attrsStr: withAttrs.attrs,
					rebuild: (a) => a.length > 0 ? `${indent}::content[${withAttrs.id}]{${a}}` : `${indent}::content[${withAttrs.id}]`
				};
				const bare = trimmed.match(/^::content\[([^\]]+)\]\s*$/);
				if (!bare || bare[1] !== unitId) return null;
				return {
					attrsStr: "",
					rebuild: (a) => a.length > 0 ? `${indent}::content[${bare[1]}]{${a}}` : `${indent}::content[${bare[1]}]`
				};
			}
			if (unitKind === "for") {
				const m = matchBracketed("for");
				if (!m || m.id !== unitId) return null;
				return {
					attrsStr: m.attrs,
					rebuild: (a) => `${indent}::for[${m.id}]{${a}}`
				};
			}
			if (unitKind === "use") {
				const m = matchBracketed("use");
				if (!m || m.id !== unitId) return null;
				return {
					attrsStr: m.attrs,
					rebuild: (a) => `${indent}::use[${m.id}]{${a}}`
				};
			}
			const m = trimmed.match(/^::import\{(.*)\}\s*$/);
			if (!m) return null;
			const fromMatch = m[1].match(/from="([^"]*)"/);
			if (!fromMatch || fromMatch[1] !== unitId) return null;
			return {
				attrsStr: m[1],
				rebuild: (a) => `${indent}::import{${a}}`
			};
		};
		const opaqueLines = collectMarkdownFencedLines(lines);
		if (lines.reduce((matches, line, index) => {
			if (!opaqueLines.has(index) && isLineInTargetRange(index, locator) && matchLine(line)) matches.push(index);
			return matches;
		}, []).length > 1 && locator?.occurrence === void 0) {
			console.warn(`[OutputSerializer] Refusing attr update for repeated ${unitKind} target "${unitId}" without a locator`);
			return content;
		}
		const selectTarget = createTargetSelector(locator, opaqueLines);
		for (let i = 0; i < lines.length; i++) {
			const info = matchLine(lines[i]);
			if (!info || !selectTarget(i)) continue;
			const pairRe = /(\w+)="([^"]*)"/g;
			const pairs = [];
			let m;
			while (m = pairRe.exec(info.attrsStr)) pairs.push({
				key: m[1],
				value: m[2]
			});
			const idx = pairs.findIndex((p) => p.key === attrName);
			if (value === null) {
				if (idx < 0) return content;
				pairs.splice(idx, 1);
			} else if (idx >= 0) pairs[idx].value = value;
			else pairs.push({
				key: attrName,
				value
			});
			const rebuilt = pairs.map((p) => `${p.key}="${p.value}"`).join(" ");
			lines[i] = info.rebuild(rebuilt);
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] ${unitKind} unit "${unitId}" not found for attr update`);
		return content;
	}
	updateTemplateInputs(content, unitKind, unitId, schema, locator) {
		const document = splitDocumentLines(content);
		const { lines } = document;
		if (refuseAmbiguousExecutableTarget(collectAmbiguousExecutableIds(lines, locator), unitId, "template inputs update")) return content;
		const target = this.findTemplateInputTarget(lines, unitKind, unitId, locator);
		if (!target) {
			console.warn(`[OutputSerializer] ${unitKind} unit "${unitId}" not found for inputs update`);
			return content;
		}
		if (!Boolean(schema?.properties && Object.keys(schema.properties).length > 0)) {
			if (target.inputsStart >= 0 && target.inputsEnd >= 0) spliceDocumentLines(document, target.inputsStart, target.inputsEnd - target.inputsStart + 1);
			return joinDocumentLines(document);
		}
		const json = JSON.stringify(schema);
		const fenceLen = computeFenceLength(json);
		const fence = makeFence(fenceLen);
		const newLines = [
			`${target.indent}${fence}inputs`,
			`${target.indent}${json}`,
			`${target.indent}${fence}`
		];
		if (target.inputsStart >= 0 && target.inputsEnd >= 0) spliceDocumentLines(document, target.inputsStart, target.inputsEnd - target.inputsStart + 1, ...newLines);
		else spliceDocumentLines(document, target.insertLine, 0, ...newLines);
		return joinDocumentLines(document);
	}
	findTemplateInputTarget(lines, unitKind, unitId, locator) {
		const selectTarget = createTargetSelector(locator, collectMarkdownFencedLines(lines));
		for (let i = 0; i < lines.length; i++) {
			const trimmed = lines[i].trim();
			if (!this.matchesTemplateInputUnitHeader(trimmed, unitKind, unitId)) continue;
			if (!selectTarget(i)) continue;
			const indent = getLeadingWhitespace(lines[i]);
			const range = unitKind === "for" ? this.findForBlockInputs(lines, i) : unitKind === "content" ? this.findContentBlockInputs(lines, i) : this.findSimpleBlockInputs(lines, i);
			if (!range) return null;
			return {
				...range,
				insertLine: i + 1,
				indent
			};
		}
		return null;
	}
	matchesTemplateInputUnitHeader(trimmed, unitKind, unitId) {
		if (unitKind === "content") return trimmed.match(/^::content\[([^\]]+)\](?:\{.*\})?\s*$/)?.[1] === unitId;
		return trimmed.match(new RegExp(`^::${unitKind}\\[([^\\]]+)\\]\\{.*\\}\\s*$`))?.[1] === unitId;
	}
	findSimpleBlockInputs(lines, headerLine) {
		let inputsStart = -1;
		let inputsEnd = -1;
		let j = headerLine + 1;
		let inFence = false;
		let activeFenceLen = 3;
		let fenceKind = "";
		while (j < lines.length) {
			const t = lines[j].trim();
			if (!inFence && t === "::") break;
			if (!inFence) {
				const inputs = /^(`{3,})inputs\s*$/.exec(t);
				const fmatch = /^(`{3,})(config|output)\s*$/.exec(t);
				if (inputs) {
					inFence = true;
					fenceKind = "inputs";
					activeFenceLen = inputs[1].length;
					inputsStart = j;
				} else if (fmatch) {
					inFence = true;
					fenceKind = fmatch[2];
					activeFenceLen = fmatch[1].length;
				}
			} else if (isClosingFence(t, activeFenceLen)) {
				if (fenceKind === "inputs") inputsEnd = j;
				inFence = false;
				fenceKind = "";
				if (inputsStart >= 0 && inputsEnd >= 0) break;
			}
			j++;
		}
		return {
			inputsStart,
			inputsEnd
		};
	}
	findContentBlockInputs(lines, headerLine) {
		let inputsStart = -1;
		let inputsEnd = -1;
		let j = headerLine + 1;
		let nestingDepth = 0;
		let forDepth = 0;
		let inFence = false;
		let activeFenceLen = 3;
		let fenceKind = "";
		let markdownFence = null;
		while (j < lines.length) {
			const t = lines[j].trim();
			if (inFence) {
				if (isClosingFence(t, activeFenceLen)) {
					if (fenceKind === "inputs") inputsEnd = j;
					inFence = false;
					fenceKind = "";
					if (inputsStart >= 0 && inputsEnd >= 0) break;
				}
				j++;
				continue;
			}
			if (markdownFence) {
				if (isMarkdownFenceClose(t, markdownFence)) markdownFence = null;
				j++;
				continue;
			}
			const inputs = /^(`{3,})inputs\s*$/.exec(t);
			const fmatch = /^(`{3,})(config|output)\s*$/.exec(t);
			const markdownFenceOpen = parseMarkdownFenceOpen(t);
			if (markdownFenceOpen && !(nestingDepth === 0 && forDepth === 0 && Boolean(inputs || fmatch))) {
				markdownFence = markdownFenceOpen;
				j++;
				continue;
			}
			if (isBlockScopedDirective(lines, j)) nestingDepth++;
			else if (FOR_HEADER_RE.test(t)) forDepth++;
			else if (FOR_CLOSE_RE.test(t)) {
				if (forDepth > 0) forDepth--;
			} else if (t === "::") {
				if (nestingDepth > 0) nestingDepth--;
				else if (forDepth === 0) break;
			}
			if (nestingDepth === 0 && forDepth === 0) {
				if (inputs) {
					inFence = true;
					fenceKind = "inputs";
					activeFenceLen = inputs[1].length;
					inputsStart = j;
				} else if (fmatch) {
					inFence = true;
					fenceKind = fmatch[2];
					activeFenceLen = fmatch[1].length;
				}
			}
			j++;
		}
		return {
			inputsStart,
			inputsEnd
		};
	}
	findForBlockInputs(lines, headerLine) {
		let inputsStart = -1;
		let inputsEnd = -1;
		let j = headerLine + 1;
		let forDepth = 0;
		let nestedBlockDepth = 0;
		let inFence = false;
		let activeFenceLen = 3;
		let capturesInputsFence = false;
		let markdownFence = null;
		while (j < lines.length) {
			const t = lines[j].trim();
			if (inFence) {
				if (isClosingFence(t, activeFenceLen)) {
					if (capturesInputsFence) inputsEnd = j;
					inFence = false;
					capturesInputsFence = false;
					if (inputsStart >= 0 && inputsEnd >= 0) break;
				}
				j++;
				continue;
			}
			if (markdownFence) {
				if (isMarkdownFenceClose(t, markdownFence)) markdownFence = null;
				j++;
				continue;
			}
			if (FOR_HEADER_RE.test(t)) forDepth++;
			else if (FOR_CLOSE_RE.test(t)) {
				if (forDepth > 0) forDepth--;
				else break;
			} else if (isBlockScopedDirective(lines, j)) nestedBlockDepth++;
			else if (t === "::" && nestedBlockDepth > 0) nestedBlockDepth--;
			const inputs = /^(`{3,})inputs\s*$/.exec(t);
			const fmatch = /^(`{3,})(config|output)\s*$/.exec(t);
			if (inputs || fmatch) {
				inFence = true;
				activeFenceLen = inputs?.[1].length ?? fmatch[1].length;
				capturesInputsFence = Boolean(inputs) && forDepth === 0 && nestedBlockDepth === 0;
				if (capturesInputsFence) inputsStart = j;
				j++;
				continue;
			}
			const markdownFenceOpen = parseMarkdownFenceOpen(t);
			if (markdownFenceOpen) {
				markdownFence = markdownFenceOpen;
				j++;
				continue;
			}
			j++;
		}
		return {
			inputsStart,
			inputsEnd
		};
	}
	/**
	* Update the config block for a `::meta[domain]` block.
	* Finds the `::meta[domain]` header and replaces (or inserts) the config fence.
	*
	* @param content  - Markdown document string
	* @param domain   - The meta domain, e.g. "module" or "config"
	* @param config   - The new config object to serialize
	*/
	updateMetaConfig(content, domain, config) {
		const configJson = JSON.stringify(config);
		const fenceLen = computeFenceLength(configJson);
		const fence = makeFence(fenceLen);
		const document = splitDocumentLines(content);
		const { lines } = document;
		const metaRe = new RegExp(`^::meta\\[${this.escapeRegex(domain)}\\]\\s*$`);
		let i = 0;
		while (i < lines.length) {
			if (!metaRe.test(lines[i].trim())) {
				i++;
				continue;
			}
			let j = i + 1;
			let inFence = false;
			let activeFenceLen = 3;
			let configStart = -1;
			let configEnd = -1;
			while (j < lines.length) {
				const t = lines[j].trim();
				if (!inFence) {
					if (t === "::") break;
					const fmatch = t.match(/^(`{3,})(config|output)\s*$/);
					if (fmatch) {
						inFence = true;
						activeFenceLen = fmatch[1].length;
						if (fmatch[2] === "config") configStart = j;
					}
				} else if (t.length === activeFenceLen && t === "`".repeat(activeFenceLen)) {
					if (configStart >= 0 && configEnd < 0) configEnd = j;
					inFence = false;
				}
				j++;
			}
			const newConfigLines = [
				`${fence}config`,
				configJson,
				fence
			];
			if (configStart >= 0 && configEnd >= 0) spliceDocumentLines(document, configStart, configEnd - configStart + 1, ...newConfigLines);
			else spliceDocumentLines(document, i + 1, 0, ...newConfigLines);
			return joinDocumentLines(document);
		}
		console.warn(`[OutputSerializer] Meta block "${domain}" not found for config update`);
		return content;
	}
};
//#endregion
export { ASSETS_REF_PREFIX, ASSET_REF_FULL_RE, ASSET_REF_RE, ATTR_PAIR_RE, BLOCK_RENDER_RE, COMPLETION_DIRECTIVE_TRIGGER_RE, COMPLETION_PLUGIN_TRIGGER_RE, COMPLETION_REF_TRIGGER_RE, COMPLETION_RENDER_TRIGGER_RE, CONTENT_HEADER_RE, DEFINE_CLOSE_RE, DEFINE_HEADER_RE, DEFINE_ID_RE, FENCE_CLOSE_RE, FENCE_OPEN_RE, FOR_CLOSE_RE, FOR_HEADER_RE, IMPORT_RE, INLINE_RENDER_RE, INPUT_REF_RE, MD_FENCE_OPEN_RE, META_HEADER_RE, OutputSerializer, REF_FULL_RE, REF_ID_RE, REF_RE, REF_STRUCTURED_RE, ReferenceResolver, TEMPLATE_INPUTS_FENCE_RE, UNIT_IDENTITY_REF_RE, USE_HEADER_RE, applyTemplateInputRefsToString, applyTemplateInputRefsToValue, buildDefaultedTemplateInputs, computeFenceLength, extractExternalRef, extractTemplateParams, flattenTree, formatUnitIdRef, getBlockingTemplateParams, getContentMemberValue, getDefinitionIdsFromText, getDocumentAssetsDir, getDocumentBundleDir, getMarkdownCodeSpanRanges, getMarkdownFencedRanges, getPathDirname, getRequiredTemplateInputs, getTemplateInputSchemaParams, hasTemplateInputRefs, inferTemplateInputSchema, isAssetRefPath, isClosingFence, isEscaped, isExternalStub, isInsideCodeSpanInLine, isInsideInlineCode, isInsideMarkdownCodeSpan, isInsideMarkdownFencedBlock, isMarkdownFenceClose, isUnitIdRef, isValidExternalOutputDescriptor, isValidExternalOutputLocator, isValidScopedMemberCandidate, makeFence, mergeTemplateParams, normalizeAssetRefPath, normalizeTemplateInputSchema, normalizeUnitIdRef, parseAssetRefPath, parseAttrs, parseDefineHeader, parseMagicMd, parseMarkdownFenceOpen, parseTemplateInputSchemaJson, registerContainerHandler, resolveDependencyRootId, resolveDocumentAssetPath, resolveImportPrivateAliasId, scanDocument, scanDocumentText, splitScopedMemberPath, substituteTemplateInputRefs, validateTemplateInputValues };
